import React, { useState, useEffect, useCallback } from 'react';
import { useApi, useAuth } from '@/contexts/AuthContext';
import EmptyState from '@/components/EmptyState';
import { Card } from '@/components/ui/card';
import { Skeleton } from '@/components/ui/skeleton';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { toast } from 'sonner';
import {
  Shield, AlertTriangle, ChevronRight, ArrowLeft, FileText, Users,
  DollarSign, Lock, Scale, Building2, Clock, CheckCircle2, XCircle,
  Plus, Search, Download, ShieldCheck, Copy
} from 'lucide-react';
import { RegistryLink } from '@/components/RegistryLink';
import { BinderTermsEditor } from '@/components/compliance/BinderTermsEditor';
import { BinderBookHint } from '@/components/compliance/BinderBookHint';
import { ResponsiveContainer, ComposedChart, Area, Line, XAxis, YAxis, Tooltip, ReferenceLine } from 'recharts';

const C = {
  navy: '#0D1B2A', saffron: '#FF9933', teal: '#00B4D8', green: '#1D9E75',
  red: '#E24B4A', amber: '#E8A020', slate: '#5C6B7A', bg: '#F5F7FA', blue: '#1A4C7C',
};

const MODULE_ICONS = {
  '01': Scale, '02': Shield, '03': Lock, '04': FileText,
  '05': Lock, '06': DollarSign, '07': Building2,
  '08': FileText, '09': Shield, '10': Scale, '11': Users,
  '12': AlertTriangle, '13': FileText, '14': Shield, '15': AlertTriangle,
};

function fmt$(v) {
  if (v >= 1e6) return `$${(v / 1e6).toFixed(1)}M`;
  if (v >= 1e3) return `$${(v / 1e3).toFixed(0)}K`;
  return `$${v?.toLocaleString() || 0}`;
}

// ─── Main Compliance Hub ───
export default function CompliancePage() {
  const [activeModule, setActiveModule] = useState(null);

  return (
    <div className="space-y-5" data-testid="compliance-page">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-semibold tracking-tight" style={{ color: C.navy, fontFamily: "'IBM Plex Sans', sans-serif" }}>
            Compliance Hub
          </h1>
          {activeModule ? (
            <div className="flex items-center gap-1.5 text-sm mt-1">
              <button onClick={() => setActiveModule(null)} className="hover:underline" style={{ color: C.blue }}>All Modules</button>
              <ChevronRight className="w-3.5 h-3.5" style={{ color: C.slate }} />
              <span style={{ color: C.navy }} className="font-medium">{activeModule.name}</span>
            </div>
          ) : (
            <p className="text-sm mt-1" style={{ color: C.slate }}>AFSL Regulatory Compliance — 15 Modules</p>
          )}
        </div>
        {activeModule && (
          <button onClick={() => setActiveModule(null)} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium hover:bg-[rgba(26,76,124,0.08)]" style={{ color: C.blue }} data-testid="compliance-back">
            <ArrowLeft className="w-4 h-4" /> Back
          </button>
        )}
      </div>

      {!activeModule ? <ComplianceOverview onSelectModule={setActiveModule} /> : (
        <>
          {activeModule.id === '01' && <IDRModule />}
          {activeModule.id === '02' && <AFCAModule />}
          {activeModule.id === '03' && <AMLModule />}
          {activeModule.id === '04' && <APRAModule />}
          {activeModule.id === '05' && <PrivacyModule />}
          {activeModule.id === '06' && <TrustAccountModule />}
          {activeModule.id === '07' && <BindingAuthorityModule />}
          {activeModule.id === '08' && <WordingsModule />}
          {activeModule.id === '09' && <ReinsuranceModule />}
          {activeModule.id === '10' && <GICodeModule />}
          {activeModule.id === '11' && <StaffModule />}
          {activeModule.id === '12' && <ConflictsModule />}
          {activeModule.id === '13' && <DDOModule />}
          {activeModule.id === '14' && <BCPModule />}
          {activeModule.id === '15' && <CPS230Module />}
        </>
      )}
    </div>
  );
}

// ─── Overview Dashboard ───
function AixiomScanCard() {
  const api = useApi();
  const { user } = useAuth();
  const [latest, setLatest] = useState(null);
  const [running, setRunning] = useState(false);
  const [expanded, setExpanded] = useState(false);
  const canRun = ['mga_admin', 'platform_owner'].includes(user?.role);

  const load = () => api.get('/compliance/aixiom/scans?limit=1').then(r => setLatest(r.data.latest)).catch(() => {});
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [api]);

  const runScan = async () => {
    setRunning(true);
    try {
      const { data } = await api.post('/compliance/aixiom/scan');
      setLatest(data);
      toast.success(`AIXIOM-C scan complete — posture ${data.posture_score}/100 (${data.rag.toUpperCase()})`);
    } catch (e) { toast.error(e?.response?.data?.detail || 'Scan failed'); }
    setRunning(false);
  };

  const ragColor = { green: C.green, amber: C.amber, red: C.red }[latest?.rag] || C.slate;
  const statusColor = { pass: C.green, warn: C.amber, fail: C.red };

  return (
    <Card className="p-5 rounded-sm bg-white" style={{ border: `1px solid ${latest ? ragColor + '55' : 'rgba(136,150,168,0.2)'}` }} data-testid="aixiom-scan-card">
      <div className="flex flex-wrap items-center gap-4">
        <div className="w-11 h-11 rounded-sm flex items-center justify-center shrink-0" style={{ background: (latest ? ragColor : C.slate) + '15' }}>
          <span className="text-lg font-bold font-mono" style={{ color: latest ? ragColor : C.slate }}>{latest ? latest.posture_score : '—'}</span>
        </div>
        <div className="flex-1 min-w-[220px]">
          <div className="flex items-center gap-2">
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>AIXIOM-C Weekly Regulatory Scan</h3>
            {latest && (
              <span className="px-1.5 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: ragColor + '18', color: ragColor }} data-testid="aixiom-rag">
                {latest.rag}
              </span>
            )}
          </div>
          <p className="text-xs mt-0.5" style={{ color: C.slate }} data-testid="aixiom-summary">
            {latest ? latest.summary : 'No scan yet — AIXIOM-C self-audits the regulatory posture every Monday, or run one now.'}
          </p>
          {latest && <p className="text-[10px] mt-1" style={{ color: C.slate }}>Week {latest.week} · run {new Date(latest.run_at).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })} · {latest.failing} fail / {latest.warning} warn across {latest.checks.length} checks</p>}
        </div>
        <div className="flex flex-col gap-1.5 shrink-0">
          {canRun && (
            <Button size="sm" onClick={runScan} disabled={running} className="rounded-sm text-white" style={{ background: C.blue }} data-testid="aixiom-run-btn">
              {running ? 'Scanning…' : 'Run Scan Now'}
            </Button>
          )}
          {latest && (
            <button onClick={() => setExpanded(e => !e)} className="text-[11px] hover:underline" style={{ color: C.blue }} data-testid="aixiom-expand">
              {expanded ? 'Hide details' : 'Show all checks'}
            </button>
          )}
        </div>
      </div>
      {latest && expanded && (
        <div className="mt-4 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5" data-testid="aixiom-checks">
          {latest.checks.map((c, i) => (
            <div key={i} className="flex items-start gap-2 text-xs py-1 border-b" style={{ borderColor: 'rgba(136,150,168,0.08)' }}>
              <span className="px-1.5 py-0.5 rounded text-[9px] font-bold uppercase shrink-0 mt-0.5" style={{ background: statusColor[c.status] + '15', color: statusColor[c.status] }}>{c.status}</span>
              <div><span className="font-medium" style={{ color: C.navy }}>{c.area}</span> <span style={{ color: C.slate }}>— {c.detail}</span></div>
            </div>
          ))}
          {latest.recommendations?.length > 0 && (
            <div className="md:col-span-2 mt-2 p-3 rounded-sm" style={{ background: C.bg }}>
              <p className="text-[10px] uppercase tracking-wider font-semibold mb-1" style={{ color: C.slate }}>AIXIOM-C recommends</p>
              {latest.recommendations.map((r, i) => <p key={i} className="text-xs" style={{ color: C.navy }}>{r}</p>)}
            </div>
          )}
        </div>
      )}
    </Card>
  );
}

function PostureTrendCard() {
  const api = useApi();
  const [scans, setScans] = useState([]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/compliance/aixiom/scans?limit=52').then(r => setScans((r.data.scans || []).slice().reverse())).catch(() => {});
  }, [api]);

  if (scans.length < 2) return null;

  const ragColor = { green: C.green, amber: C.amber, red: C.red };
  const data = scans.map(s => ({
    label: new Date(s.run_at).toLocaleDateString('en-AU', { day: '2-digit', month: 'short' }),
    week: s.week,
    score: s.posture_score,
    rag: s.rag,
  }));
  const latest = data[data.length - 1];
  const delta = latest.score - data[0].score;

  return (
    <Card className="p-5 rounded-sm bg-white" style={{ border: '1px solid rgba(136,150,168,0.2)' }} data-testid="posture-trend-card">
      <div className="flex items-center justify-between mb-1">
        <div>
          <p className="text-[10px] uppercase tracking-[0.15em]" style={{ color: C.slate, fontFamily: 'Georgia, serif' }}>Posture trend</p>
          <h3 className="text-sm font-semibold mt-0.5" style={{ color: C.navy }}>AIXIOM-C weekly regulatory posture over time</h3>
        </div>
        <div className="text-right">
          <p className="text-2xl font-light tracking-tight" style={{ color: ragColor[latest.rag] || C.navy, fontVariantNumeric: 'tabular-nums' }} data-testid="posture-trend-latest">{latest.score}</p>
          <p className="text-[10px]" style={{ color: delta >= 0 ? C.green : C.red }}>{delta >= 0 ? '▲' : '▼'} {Math.abs(delta)} pts since {data[0].label}</p>
        </div>
      </div>
      <div className="h-44 mt-3" data-testid="posture-trend-chart">
        <ResponsiveContainer width="100%" height="100%">
          <ComposedChart data={data} margin={{ top: 8, right: 8, bottom: 0, left: -22 }}>
            <defs>
              <linearGradient id="postureFill" x1="0" y1="0" x2="0" y2="1">
                <stop offset="0%" stopColor={C.teal} stopOpacity={0.22} />
                <stop offset="100%" stopColor={C.teal} stopOpacity={0.02} />
              </linearGradient>
            </defs>
            <XAxis dataKey="label" tick={{ fontSize: 10, fill: C.slate }} axisLine={{ stroke: 'rgba(136,150,168,0.25)' }} tickLine={false} />
            <YAxis domain={[0, 100]} ticks={[0, 25, 50, 65, 85, 100]} tick={{ fontSize: 10, fill: C.slate }} axisLine={false} tickLine={false} />
            <ReferenceLine y={85} stroke={C.green} strokeDasharray="4 4" strokeOpacity={0.5} />
            <ReferenceLine y={65} stroke={C.amber} strokeDasharray="4 4" strokeOpacity={0.5} />
            <Tooltip
              contentStyle={{ borderRadius: 2, border: '1px solid rgba(136,150,168,0.3)', fontSize: 12 }}
              formatter={(v, _n, p) => [`${v}/100 (${(p.payload.rag || '').toUpperCase()})`, 'Posture']}
              labelFormatter={(l, p) => `${l} · Week ${p?.[0]?.payload?.week || ''}`}
            />
            <Area type="monotone" dataKey="score" stroke="none" fill="url(#postureFill)" isAnimationActive={false} />
            <Line type="monotone" dataKey="score" stroke={C.navy} strokeWidth={1.5} isAnimationActive={false}
              dot={({ cx, cy, payload, index }) => <circle key={index} cx={cx} cy={cy} r={3.5} fill={ragColor[payload.rag] || C.slate} stroke="#fff" strokeWidth={1.5} />} />
          </ComposedChart>
        </ResponsiveContainer>
      </div>
      <div className="flex items-center gap-4 mt-2 text-[10px]" style={{ color: C.slate }}>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: C.green }} /> ≥85 Green</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: C.amber }} /> 65–84 Amber</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 rounded-full" style={{ background: C.red }} /> &lt;65 Red</span>
        <span className="ml-auto">Scheduled scan runs every Monday · manual scans also plotted</span>
      </div>
    </Card>
  );
}

function ComplianceOverview({ onSelectModule }) {
  const api = useApi();
  const [data, setData] = useState(null);
  const [denied, setDenied] = useState(false);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/compliance/overview').then(r => setData(r.data))
      .catch(err => { if (err?.response?.status === 403) setDenied(true); })
      .finally(() => setLoading(false));
  }, [api]);

  if (loading) return <div className="space-y-3">{[...Array(4)].map((_, i) => <Skeleton key={i} className="h-20 rounded-sm" />)}</div>;
  if (denied) return (
    <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="compliance-overview-restricted">
      <p className="text-xs" style={{ color: C.slate }}>
        The compliance score and module register are restricted to the MGA admin and the platform
        owner. Complaints, conduct scans and the modules you own are on the tabs above.
      </p>
    </Card>
  );
  if (!data) return null;

  const scoreColor = data.overall_score >= 80 ? C.green : data.overall_score >= 50 ? C.amber : C.red;

  return (
    <div className="space-y-5" data-testid="compliance-overview">
      <AixiomScanCard />
      <PostureTrendCard />
      <AuditTile />
      {/* Score Bar */}
      <Card className="p-5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="flex items-center justify-between mb-3">
          <div>
            <h2 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Compliance Score</h2>
            <p className="text-xs mt-0.5" style={{ color: C.slate }}>{data.compliant_modules}/{data.total_modules} modules compliant</p>
          </div>
          <span className="text-3xl font-bold" style={{ color: scoreColor }}>{data.overall_score}%</span>
        </div>
        <div className="w-full h-2.5 rounded-full" style={{ background: C.bg }}>
          <div className="h-2.5 rounded-full transition-all duration-700" style={{ width: `${data.overall_score}%`, background: scoreColor }} />
        </div>
      </Card>

      {/* Module Grid */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-3">
        {data.modules.map(mod => {
          const Icon = MODULE_ICONS[mod.id] || Shield;
          const statusColor = mod.status === 'compliant' ? C.green : C.amber;
          return (
            <button key={mod.id} onClick={() => onSelectModule(mod)} className="text-left group" data-testid={`module-${mod.id}`}>
              <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white hover:border-[#1A4C7C] hover:shadow-md transition-all duration-200 h-full">
                <div className="flex items-start justify-between mb-2">
                  <div className="w-8 h-8 rounded-sm flex items-center justify-center" style={{ background: statusColor + '12' }}>
                    <Icon className="w-4 h-4" style={{ color: statusColor }} />
                  </div>
                  <span className="text-[10px] px-1.5 py-0.5 rounded font-medium uppercase" style={{ background: statusColor + '15', color: statusColor }}>
                    {mod.status === 'compliant' ? 'OK' : 'ACTION'}
                  </span>
                </div>
                <h3 className="text-sm font-semibold mb-0.5" style={{ color: C.navy }}>M{mod.id}: {mod.name}</h3>
                <p className="text-[11px] mb-2" style={{ color: C.slate }}>{mod.regulator}</p>
                <div className="flex items-center justify-between pt-2 border-t border-[rgba(136,150,168,0.1)]">
                  <span className="text-xs" style={{ color: C.slate }}>{mod.kpi}</span>
                  {mod.open_items > 0 && (
                    <span className="text-xs font-medium px-1.5 py-0.5 rounded" style={{ background: C.amber + '15', color: C.amber }}>{mod.open_items} open</span>
                  )}
                </div>
              </Card>
            </button>
          );
        })}
      </div>
    </div>
  );
}

function AuditTile() {
  const api = useApi();
  const { user } = useAuth();
  const [run, setRun] = useState(null);
  const [busy, setBusy] = useState(false);
  const canRun = ['mga_admin', 'platform_owner'].includes(user?.role);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (canRun) api.get('/platform/audit/latest').then(r => setRun(r.data)).catch(err => { console.error('API error:', err?.message); });
  }, [api, canRun]);
  if (!canRun || !run || !run.checks) return null;
  const green = run.all_green;
  const color = green ? C.green : C.red;
  const failing = (run.checks || []).filter(c => !c.pass);
  const runNow = async () => {
    setBusy(true);
    try { const { data } = await api.post('/platform/audit/run'); setRun(data); }
    catch (err) { console.error('API error:', err?.message); }
    setBusy(false);
  };
  return (
    <Card className="p-4 rounded-sm border bg-white" style={{ borderColor: color + '55' }} data-testid="audit-tile">
      <div className="flex items-center justify-between flex-wrap gap-2">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 rounded-sm flex items-center justify-center" style={{ background: color + '12' }}>
            {green ? <ShieldCheck className="w-5 h-5" style={{ color }} /> : <AlertTriangle className="w-5 h-5" style={{ color }} />}
          </div>
          <div>
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Platform Integrity Audit</h3>
            <p className="text-xs" style={{ color: C.slate }}>
              <span className="font-semibold" style={{ color }} data-testid="audit-tile-status">{green ? 'ALL GREEN' : `${run.failure_count} CHECK(S) RED`}</span>
              {' '}· {(run.checks || []).length} checks · last run {new Date(run.at).toLocaleString('en-AU')} · re-runs monthly on the 1st
            </p>
          </div>
        </div>
        <button onClick={runNow} disabled={busy} data-testid="audit-tile-run-btn"
          className="px-3 py-1.5 rounded-sm text-xs font-semibold text-white disabled:opacity-50" style={{ background: C.navy }}>
          {busy ? 'Running…' : 'Run Now'}
        </button>
      </div>
      {!green && (
        <div className="mt-2 flex flex-wrap gap-1.5" data-testid="audit-tile-failures">
          {failing.map(f => (
            <span key={f.name} className="px-1.5 py-0.5 rounded text-[10px] font-semibold" style={{ background: C.red + '12', color: C.red }}>{f.name}: {f.count}</span>
          ))}
        </div>
      )}
    </Card>
  );
}

// ─── Module 01: IDR Complaints ───
function IDRModule() {
  const api = useApi();
  const { user } = useAuth();
  const canAct = ['mga_admin', 'platform_owner'].includes(user?.role);
  const [complaints, setComplaints] = useState([]);
  const [dashboard, setDashboard] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [rowAction, setRowAction] = useState(null);
  const [resolutionText, setResolutionText] = useState('');
  const [outcome, setOutcome] = useState('upheld');
  const [afcaRef, setAfcaRef] = useState('');
  const [actionErr, setActionErr] = useState('');
  const [form, setForm] = useState({ complainant_name: '', complainant_type: 'broker', nature: 'other', description: '', source: 'portal', policy_number: '', claim_number: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    Promise.all([
      api.get('/compliance/complaints'),
      api.get('/compliance/complaints/dashboard'),
    ]).then(([c, d]) => { setComplaints(c.data); setDashboard(d.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    await api.post('/compliance/complaints', form);
    setShowCreate(false);
    setForm({ complainant_name: '', complainant_type: 'broker', nature: 'other', description: '', source: 'portal', policy_number: '', claim_number: '' });
    load();
  };

  const runAction = async (fn) => {
    setActionErr('');
    try { await fn(); setRowAction(null); setResolutionText(''); setAfcaRef(''); load(); }
    catch (e) { setActionErr(e?.response?.data?.detail || 'Action failed'); }
  };

  if (loading) return <Skeleton className="h-40 rounded-sm" />;

  return (
    <div className="space-y-4" data-testid="idr-module">
      {/* KPIs */}
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MiniKPI label="Total" value={dashboard.total} color={C.blue} />
          <MiniKPI label="Open" value={dashboard.open + dashboard.under_review} color={C.amber} />
          <MiniKPI label="Resolved" value={dashboard.resolved} color={C.green} />
          <MiniKPI label="Avg Days" value={`${dashboard.avg_resolution_days}d`} color={C.teal} />
          <MiniKPI label="Breach Rate" value={`${dashboard.breach_rate}%`} color={dashboard.breach_rate > 0 ? C.red : C.green} />
        </div>
      )}

      {/* Actions */}
      <div className="flex gap-2">
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.blue }} data-testid="create-complaint-btn">
          <Plus className="w-4 h-4" /> New Complaint
        </button>
      </div>

      {/* Create Form */}
      {showCreate && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3">
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Log New Complaint</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Complainant Name" value={form.complainant_name} onChange={e => setForm({...form, complainant_name: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" data-testid="complaint-name-input" />
            <select value={form.complainant_type} onChange={e => setForm({...form, complainant_type: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" data-testid="complaint-type-select">
              <option value="broker">Broker</option><option value="insured">Insured</option><option value="third_party">Third Party</option>
            </select>
            <select value={form.nature} onChange={e => setForm({...form, nature: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" data-testid="complaint-nature-select">
              <option value="claims_handling">Claims Handling</option><option value="underwriting_decision">Underwriting Decision</option>
              <option value="premium">Premium</option><option value="service">Service</option><option value="documentation">Documentation</option><option value="other">Other</option>
            </select>
            <select value={form.source} onChange={e => setForm({...form, source: e.target.value})} className="px-3 py-2 text-sm border rounded-sm">
              <option value="portal">Portal</option><option value="email">Email</option><option value="phone">Phone</option>
              <option value="claims_dispute">Claims Dispute</option><option value="uw_dispute">UW Dispute</option><option value="regulatory_referral">Regulatory</option>
            </select>
            <input placeholder="Policy Number (optional)" value={form.policy_number} onChange={e => setForm({...form, policy_number: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
            <input placeholder="Claim Number (optional)" value={form.claim_number} onChange={e => setForm({...form, claim_number: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
          </div>
          <textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-sm h-20" data-testid="complaint-description-input" />
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-4 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.green }} data-testid="submit-complaint-btn">Submit</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-sm text-sm font-medium" style={{ color: C.slate }}>Cancel</button>
          </div>
        </Card>
      )}

      {/* Complaints List */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Complaints Queue</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm" data-testid="complaints-table">
            <thead>
              <tr style={{ background: C.bg }} className="border-b border-[rgba(136,150,168,0.15)]">
                {['Reference', 'Complainant', 'Nature', 'Source', 'Status', 'Days Left', 'Created', 'Actions'].map(h => (
                  <th key={h} className="text-left py-2 px-3 text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {complaints.length === 0 ? (
                <tr><td colSpan={8} className="p-0"><EmptyState type="complaints" compact /></td></tr>
              ) : complaints.map(c => {
                const deadline = new Date(c.idr_deadline);
                const daysLeft = Math.ceil((deadline - new Date()) / 86400000);
                const urgencyColor = daysLeft <= 5 ? C.red : daysLeft <= 15 ? C.amber : C.green;
                const statusColors = { open: C.amber, under_review: C.teal, resolved: C.green, escalated_to_afca: C.red, idr_delay_issued: C.amber };
                const activeComplaint = !['resolved', 'escalated_to_afca'].includes(c.status);
                return (
                  <React.Fragment key={c.complaint_id}>
                  <tr className="border-b border-[rgba(136,150,168,0.08)] hover:bg-[#F5F7FA]" data-testid={`complaint-row-${c.complaint_id}`}>
                    <td className="py-2 px-3 font-mono text-xs" style={{ color: C.blue }}>{c.reference}</td>
                    <td className="py-2 px-3 text-xs font-medium" style={{ color: C.navy }}>{c.complainant_name}</td>
                    <td className="py-2 px-3 text-xs capitalize" style={{ color: C.slate }}>{c.nature?.replace(/_/g, ' ')}</td>
                    <td className="py-2 px-3 text-xs capitalize" style={{ color: C.slate }}>{c.source?.replace(/_/g, ' ')}</td>
                    <td className="py-2 px-3">
                      <span className="px-1.5 py-0.5 rounded text-[10px] font-medium uppercase" style={{ background: (statusColors[c.status] || C.slate) + '15', color: statusColors[c.status] || C.slate }}>{c.status?.replace(/_/g, ' ')}</span>
                    </td>
                    <td className="py-2 px-3">
                      {c.status !== 'resolved' && c.status !== 'escalated_to_afca' ? (
                        <span className="flex items-center gap-1 text-xs font-medium" style={{ color: urgencyColor }}>
                          <Clock className="w-3 h-3" /> {daysLeft}d
                        </span>
                      ) : <span className="text-xs" style={{ color: C.green }}>—</span>}
                    </td>
                    <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{new Date(c.created_at).toLocaleDateString('en-AU')}</td>
                    <td className="py-2 px-3">
                      <div className="flex flex-wrap gap-1 items-center">
                        <button onClick={() => { setRowAction(rowAction?.id === c.complaint_id && rowAction?.type === 'timeline' ? null : { id: c.complaint_id, type: 'timeline' }); setActionErr(''); }}
                          className="px-2 py-1 rounded text-[10px] font-medium" style={{ color: C.blue, border: '1px solid rgba(26,76,124,0.3)', background: rowAction?.id === c.complaint_id && rowAction?.type === 'timeline' ? 'rgba(26,76,124,0.08)' : 'white' }}
                          data-testid={`complaint-timeline-${c.complaint_id}`}>Timeline</button>
                        {canAct && activeComplaint && (
                          <>
                          {!c.acknowledged_at && (
                            <button onClick={() => runAction(() => api.post(`/compliance/complaints/${c.complaint_id}/acknowledge`))}
                              className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.teal }}
                              data-testid={`complaint-ack-${c.complaint_id}`}>Acknowledge</button>
                          )}
                          {c.status === 'open' && (
                            <button onClick={() => runAction(() => api.put(`/compliance/complaints/${c.complaint_id}`, { status: 'under_review' }))}
                              className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.blue }}
                              data-testid={`complaint-review-${c.complaint_id}`}>Start Review</button>
                          )}
                          <button onClick={() => { setRowAction(rowAction?.id === c.complaint_id && rowAction?.type === 'resolve' ? null : { id: c.complaint_id, type: 'resolve' }); setActionErr(''); }}
                            className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.green }}
                            data-testid={`complaint-resolve-${c.complaint_id}`}>Resolve</button>
                          <button onClick={() => { setRowAction(rowAction?.id === c.complaint_id && rowAction?.type === 'afca' ? null : { id: c.complaint_id, type: 'afca' }); setActionErr(''); }}
                            className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.red }}
                            data-testid={`complaint-afca-${c.complaint_id}`}>AFCA</button>
                          </>
                        )}
                        {c.status === 'resolved' && (
                          <span className="text-[10px] capitalize" style={{ color: C.green }}>{(c.outcome || 'resolved').replace(/_/g, ' ')}</span>
                        )}
                        {canAct && c.status === 'resolved' && c.resolution_description && (
                          <button onClick={async () => {
                            try {
                              const resp = await api.post(`/compliance/complaints/${c.complaint_id}/idr-letter`, {
                                decision: c.resolution_description,
                                upheld: c.outcome === 'upheld',
                                partially_upheld: c.outcome === 'partially_upheld',
                                reasons: [c.resolution_description],
                              }, { responseType: 'blob' });
                              const url = URL.createObjectURL(resp.data); const a = document.createElement('a');
                              a.href = url; a.download = `IDR_Response_${c.reference}.pdf`; a.click(); URL.revokeObjectURL(url);
                            } catch { setActionErr('Could not generate the IDR letter'); }
                          }} className="px-2 py-1 rounded text-[10px] font-medium" style={{ color: C.navy, border: '1px solid rgba(18,40,63,0.3)' }}
                            data-testid={`complaint-idr-letter-${c.complaint_id}`} title="IDR Final Response letter (RG 271) — PDF">IDR Letter</button>
                        )}
                        {c.status === 'escalated_to_afca' && (
                          <span className="text-[10px] font-mono" style={{ color: C.red }}>{c.afca_reference || 'AFCA'}</span>
                        )}
                      </div>
                    </td>
                  </tr>
                  {rowAction?.id === c.complaint_id && rowAction.type === 'timeline' && (
                    <tr><td colSpan={8} className="px-4 py-3" style={{ background: '#FAFBFC' }}>
                      <ComplaintTimeline complaint={c} />
                    </td></tr>
                  )}
                  {rowAction?.id === c.complaint_id && rowAction.type !== 'timeline' && (
                    <tr><td colSpan={8} className="px-4 py-3" style={{ background: '#FAFBFC' }}>
                      {rowAction.type === 'resolve' ? (
                        <div className="space-y-2" data-testid={`complaint-resolve-form-${c.complaint_id}`}>
                          <p className="text-xs font-semibold" style={{ color: C.navy }}>Resolve {c.reference}</p>
                          <textarea value={resolutionText} onChange={e => setResolutionText(e.target.value)} placeholder="Resolution description (what was decided and communicated to the complainant)"
                            className="w-full px-3 py-2 text-xs border rounded-sm h-16" data-testid="complaint-resolution-input" />
                          <div className="flex items-center gap-2">
                            <select value={outcome} onChange={e => setOutcome(e.target.value)} className="px-2 py-1.5 text-xs border rounded-sm" data-testid="complaint-outcome-select">
                              <option value="upheld">Upheld</option><option value="partially_upheld">Partially Upheld</option><option value="not_upheld">Not Upheld</option>
                            </select>
                            <button disabled={!resolutionText.trim()}
                              onClick={() => runAction(() => api.put(`/compliance/complaints/${c.complaint_id}`, { status: 'resolved', resolution_description: resolutionText.trim(), outcome }))}
                              className="px-3 py-1.5 rounded-sm text-xs font-semibold text-white disabled:opacity-40" style={{ background: C.green }}
                              data-testid="complaint-resolve-confirm">Mark Resolved</button>
                            <button onClick={() => setRowAction(null)} className="px-2 py-1.5 text-xs" style={{ color: C.slate }}>Cancel</button>
                          </div>
                        </div>
                      ) : (
                        <div className="space-y-2" data-testid={`complaint-afca-form-${c.complaint_id}`}>
                          <p className="text-xs font-semibold" style={{ color: C.navy }}>Escalate {c.reference} to AFCA (EDR)</p>
                          <div className="flex items-center gap-2">
                            <input value={afcaRef} onChange={e => setAfcaRef(e.target.value)} placeholder="AFCA case reference"
                              className="px-3 py-1.5 text-xs border rounded-sm w-56" data-testid="complaint-afca-ref-input" />
                            <button disabled={!afcaRef.trim()}
                              onClick={() => runAction(() => api.post(`/compliance/complaints/${c.complaint_id}/escalate-afca`, { afca_reference: afcaRef.trim() }))}
                              className="px-3 py-1.5 rounded-sm text-xs font-semibold text-white disabled:opacity-40" style={{ background: C.red }}
                              data-testid="complaint-afca-confirm">Escalate</button>
                            <button onClick={() => setRowAction(null)} className="px-2 py-1.5 text-xs" style={{ color: C.slate }}>Cancel</button>
                          </div>
                        </div>
                      )}
                      {actionErr && <p className="text-xs mt-1" style={{ color: C.red }} data-testid="complaint-action-error">{actionErr}</p>}
                    </td></tr>
                  )}
                  </React.Fragment>
                );
              })}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Complaint Timeline (full history: lodgement → acknowledgement → review → resolution/EDR) ───
const TIMELINE_EVENT_META = {
  lodged: { color: C.blue, label: 'Lodged' },
  acknowledged: { color: C.teal, label: 'Acknowledged' },
  under_review: { color: C.teal, label: 'Under Review' },
  idr_delay_issued: { color: C.amber, label: 'IDR Delay Issued' },
  note: { color: C.slate, label: 'Note' },
  resolved: { color: C.green, label: 'Resolved' },
  escalated_to_afca: { color: C.red, label: 'Escalated to AFCA' },
  open: { color: C.amber, label: 'Reopened' },
};

function buildComplaintTimeline(c) {
  const events = [];
  const seen = new Set();
  (c.history || []).forEach(h => { events.push({ at: h.at, event: h.event, detail: h.detail, by: h.by }); seen.add(h.event); });
  // Synthesize canonical events for complaints lodged before history tracking existed
  if (!seen.has('lodged')) events.push({ at: c.day0 || c.created_at, event: 'lodged', detail: `Complaint lodged via ${(c.source || 'portal').replace(/_/g, ' ')} — nature: ${(c.nature || 'other').replace(/_/g, ' ')}`, by: c.handler });
  if (c.acknowledged_at && !seen.has('acknowledged')) events.push({ at: c.acknowledged_at, event: 'acknowledged', detail: 'Acknowledgement issued to complainant (RG 271)' });
  if (c.resolution_date && !seen.has('resolved')) events.push({ at: c.resolution_date, event: 'resolved', detail: `Resolved — outcome: ${(c.outcome || 'recorded').replace(/_/g, ' ')}${c.resolution_description ? `. ${c.resolution_description}` : ''}` });
  if (c.status === 'escalated_to_afca' && !seen.has('escalated_to_afca')) events.push({ at: c.updated_at, event: 'escalated_to_afca', detail: `Escalated to AFCA (EDR)${c.afca_reference ? ` — case reference ${c.afca_reference}` : ''}` });
  (c.notes || []).forEach(n => events.push({ at: n.at, event: 'note', detail: n.text, by: n.by }));
  events.sort((a, b) => new Date(a.at) - new Date(b.at));
  return events;
}

function ComplaintTimeline({ complaint: c }) {
  const events = buildComplaintTimeline(c);
  const deadline = new Date(c.idr_deadline);
  const daysLeft = Math.ceil((deadline - new Date()) / 86400000);
  const closed = ['resolved', 'escalated_to_afca'].includes(c.status);
  return (
    <div data-testid={`complaint-timeline-panel-${c.complaint_id}`}>
      <div className="flex flex-wrap items-center gap-2 mb-3">
        <span className="font-mono text-xs font-semibold" style={{ color: C.blue }}>{c.reference}</span>
        <span className="text-xs" style={{ color: C.navy }}>{c.complainant_name}</span>
        <span className="text-[10px] capitalize px-1.5 py-0.5 rounded" style={{ background: 'rgba(136,150,168,0.12)', color: C.slate }}>{(c.nature || '').replace(/_/g, ' ')}</span>
        {!closed && (
          <span className="text-[10px] font-medium px-1.5 py-0.5 rounded" style={{ background: (daysLeft <= 5 ? C.red : daysLeft <= 15 ? C.amber : C.green) + '15', color: daysLeft <= 5 ? C.red : daysLeft <= 15 ? C.amber : C.green }}>
            IDR deadline: {deadline.toLocaleDateString('en-AU')} ({daysLeft < 0 ? 'OVERDUE' : `${daysLeft}d left`})
          </span>
        )}
      </div>
      {c.description && <p className="text-[11px] mb-3 italic" style={{ color: C.slate }}>"{c.description}"</p>}
      <div className="space-y-0">
        {events.map((e, i) => {
          const meta = TIMELINE_EVENT_META[e.event] || { color: C.slate, label: e.event?.replace(/_/g, ' ') };
          return (
            <div key={i} className="flex gap-3" data-testid={`timeline-event-${c.complaint_id}-${i}`}>
              <div className="flex flex-col items-center">
                <span className="w-2.5 h-2.5 rounded-full mt-1 shrink-0" style={{ background: meta.color }} />
                {i < events.length - 1 && <span className="w-px flex-1" style={{ background: 'rgba(136,150,168,0.25)' }} />}
              </div>
              <div className="pb-3 min-w-0">
                <div className="flex flex-wrap items-center gap-2">
                  <span className="text-[10px] font-semibold uppercase tracking-wider" style={{ color: meta.color }}>{meta.label}</span>
                  <span className="text-[10px]" style={{ color: C.slate }}>{e.at ? new Date(e.at).toLocaleString('en-AU', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' }) : '—'}</span>
                  {e.by && <span className="text-[10px]" style={{ color: '#64748B' }}>by {e.by}</span>}
                </div>
                <p className="text-xs mt-0.5" style={{ color: C.navy }}>{e.detail}</p>
              </div>
            </div>
          );
        })}
      </div>
    </div>
  );
}

// ─── Module 02: AFCA EDR ───
function AFCAModule() {
  const api = useApi();
  const [config, setConfig] = useState(null);
  const [escalations, setEscalations] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([
      api.get('/compliance/afca/config'),
      api.get('/compliance/afca/escalations'),
    ]).then(([c, e]) => { setConfig(c.data); setEscalations(e.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <Skeleton className="h-40 rounded-sm" />;

  return (
    <div className="space-y-4" data-testid="afca-module">
      <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>AFCA Membership</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
          <DetailRow label="Member ID" value={config?.member_id || 'Not Set'} />
          <DetailRow label="Renewal Date" value={config?.renewal_date || 'Not Set'} />
          <DetailRow label="Annual Levy" value={config?.annual_levy ? fmt$(config.annual_levy) : 'Not Set'} />
          <DetailRow label="Portal" value={config?.portal_url || 'afca.org.au'} />
        </div>
      </Card>
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>AFCA Escalations ({escalations.length})</h3>
        </div>
        <div className="p-4">
          {escalations.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: C.slate }}>No AFCA escalations</p>
          ) : escalations.map(e => (
            <div key={e.complaint_id} className="flex items-center justify-between py-2 border-b border-[rgba(136,150,168,0.08)]">
              <div><span className="font-mono text-xs" style={{ color: C.blue }}>{e.reference}</span><span className="text-xs ml-2" style={{ color: C.slate }}>{e.complainant_name}</span></div>
              <span className="text-xs font-mono" style={{ color: C.red }}>{e.afca_reference}</span>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Module 03: AML/CTF ───
function AMLModule() {
  const api = useApi();
  const [dashboard, setDashboard] = useState(null);
  const [records, setRecords] = useState([]);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    Promise.all([
      api.get('/compliance/aml/dashboard'),
      api.get('/compliance/aml/cdd'),
    ]).then(([d, r]) => { setDashboard(d.data); setRecords(r.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <Skeleton className="h-40 rounded-sm" />;

  return (
    <div className="space-y-4" data-testid="aml-module">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
          <MiniKPI label="Total Records" value={dashboard.total_records} color={C.blue} />
          <MiniKPI label="Pending" value={dashboard.pending} color={C.amber} />
          <MiniKPI label="Cleared" value={dashboard.clear} color={C.green} />
          <MiniKPI label="Completion" value={`${dashboard.completion_rate}%`} color={C.teal} />
          <MiniKPI label="SMRs Filed" value={dashboard.smr_count} color={dashboard.smr_count > 0 ? C.red : C.green} />
        </div>
      )}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]">
          <h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>CDD Records</h3>
        </div>
        <div className="p-4">
          {records.length === 0 ? (
            <p className="text-sm text-center py-4" style={{ color: C.slate }}>No CDD records yet. Records are created when entities are onboarded.</p>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr style={{ background: C.bg }} className="border-b border-[rgba(136,150,168,0.15)]">
                  {['Entity', 'ABN', 'Level', 'Sanctions', 'PEP', 'Status', 'Screened'].map(h => (
                    <th key={h} className="text-left py-2 px-3 text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {records.map(r => (
                  <tr key={r.cdd_id} className="border-b border-[rgba(136,150,168,0.08)]">
                    <td className="py-2 px-3 text-xs font-medium" style={{ color: C.navy }}>{r.entity_name}</td>
                    <td className="py-2 px-3 text-xs font-mono"><RegistryLink abn={r.abn} style={{ color: C.slate }} /></td>
                    <td className="py-2 px-3 text-xs capitalize" style={{ color: C.slate }}>{r.cdd_level}</td>
                    <td className="py-2 px-3"><StatusDot status={r.sanctions_check} /></td>
                    <td className="py-2 px-3"><StatusDot status={r.pep_check} /></td>
                    <td className="py-2 px-3"><StatusDot status={r.status} /></td>
                    <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{r.last_screened ? new Date(r.last_screened).toLocaleDateString('en-AU') : '—'}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </Card>
    </div>
  );
}

// ─── Module 04: APRA Form 701 ───
function APRAModule() {
  const api = useApi();
  const [forms, setForms] = useState([]);
  const [loading, setLoading] = useState(true);
  const [generating, setGenerating] = useState(false);
  const [confirming, setConfirming] = useState(null);
  const [confNo, setConfNo] = useState('');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    api.get('/compliance/apra/form701').then(r => setForms(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const generate = async () => {
    setGenerating(true);
    await api.post('/compliance/apra/form701').catch(err => { console.error('API error:', err?.message); });
    setGenerating(false);
    load();
  };

  const setStatus = async (formId, status, confirmation = '') => {
    try {
      await api.put(`/compliance/apra/form701/${formId}`, { status, confirmation_number: confirmation });
      toast.success(status === 'lodged' ? 'Return marked as lodged with APRA' : 'Return marked as submitted');
      setConfirming(null); setConfNo('');
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  if (loading) return <Skeleton className="h-40 rounded-sm" />;

  return (
    <div className="space-y-4" data-testid="apra-module">
      <div className="flex gap-2">
        <button onClick={generate} disabled={generating} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium text-white disabled:opacity-50" style={{ background: C.blue }} data-testid="generate-form701-btn">
          <FileText className="w-4 h-4" /> {generating ? 'Generating...' : 'Generate Form 701'}
        </button>
      </div>
      {forms.length === 0 ? (
        <Card className="p-8 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white text-center">
          <FileText className="w-8 h-8 mx-auto mb-2 opacity-40" style={{ color: C.slate }} />
          <p className="text-sm" style={{ color: C.slate }}>No Form 701 reports generated yet</p>
        </Card>
      ) : forms.map(f => (
        <Card key={f.form_id} className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <div className="flex items-center justify-between mb-3">
            <div>
              <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Quarter: {f.quarter_end}</h3>
              <p className="text-xs" style={{ color: C.slate }}>{f.mga_name} — AFSL: {f.afsl_number}</p>
            </div>
            <span className="px-2 py-0.5 rounded text-[11px] font-medium uppercase" style={{
              background: (f.status === 'lodged' ? C.green : f.status === 'submitted' ? C.teal : C.amber) + '15',
              color: f.status === 'lodged' ? C.green : f.status === 'submitted' ? C.teal : C.amber
            }}>{f.status}</span>
          </div>
          <div className="flex flex-wrap items-center gap-2 mb-3">
            <a href={`${process.env.REACT_APP_BACKEND_URL}/api/compliance/apra/form701/${f.form_id}/pack`}
              target="_blank" rel="noopener noreferrer" data-testid={`form701-pack-${f.form_id}`}
              className="px-2.5 py-1.5 rounded-sm text-[11px] font-semibold text-white hover:opacity-90"
              style={{ background: '#7C5CBF' }} title="Download the pre-filled lodgement pack (PDF + CSV, ZIP)">
              Lodgement Pack
            </a>
            {f.status === 'draft' && (
              <button onClick={() => setStatus(f.form_id, 'submitted')} data-testid={`form701-submit-${f.form_id}`}
                className="px-2.5 py-1.5 rounded-sm text-[11px] font-semibold border hover:opacity-80"
                style={{ color: C.teal, borderColor: 'rgba(0,180,216,0.4)' }}>Mark Submitted</button>
            )}
            {f.status !== 'lodged' && (confirming === f.form_id ? (
              <span className="flex items-center gap-1.5">
                <Input value={confNo} onChange={e => setConfNo(e.target.value)} placeholder="APRA confirmation no."
                  className="h-7 w-44 text-xs rounded-sm" data-testid="form701-confirmation-input" />
                <button onClick={() => setStatus(f.form_id, 'lodged', confNo)} disabled={!confNo.trim()} data-testid="form701-lodge-confirm"
                  className="px-2 py-1.5 rounded-sm text-[11px] font-semibold text-white disabled:opacity-40" style={{ background: C.green }}>✓</button>
                <button onClick={() => setConfirming(null)} className="text-xs" style={{ color: C.slate }}>✕</button>
              </span>
            ) : (
              <button onClick={() => setConfirming(f.form_id)} data-testid={`form701-lodge-${f.form_id}`}
                className="px-2.5 py-1.5 rounded-sm text-[11px] font-semibold border hover:opacity-80"
                style={{ color: C.green, borderColor: 'rgba(29,158,117,0.4)' }}>Mark Lodged</button>
            ))}
            {f.status === 'lodged' && f.confirmation_number && (
              <span className="text-[11px] font-mono" style={{ color: C.green }}>APRA ref: {f.confirmation_number}</span>
            )}
          </div>
          <div className="grid grid-cols-3 md:grid-cols-5 gap-3 text-center mb-3">
            <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Policies</p><p className="text-sm font-semibold" style={{ color: C.navy }}>{f.totals.policy_count.toLocaleString()}</p></div>
            <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>GWP</p><p className="text-sm font-semibold" style={{ color: C.navy }}>{fmt$(f.totals.gross_premium)}</p></div>
            <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Sum Insured</p><p className="text-sm font-semibold" style={{ color: C.navy }}>{fmt$(f.totals.sum_insured)}</p></div>
            <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Claims Paid</p><p className="text-sm font-semibold" style={{ color: C.navy }}>{fmt$(f.totals.claims_paid)}</p></div>
            <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Outstanding</p><p className="text-sm font-semibold" style={{ color: C.navy }}>{fmt$(f.totals.claims_outstanding)}</p></div>
          </div>
          <details className="text-xs">
            <summary className="cursor-pointer font-medium py-1" style={{ color: C.blue }}>View {f.classes?.length || 0} classes</summary>
            <div className="mt-2 overflow-x-auto">
              <table className="w-full text-xs">
                <thead><tr style={{ background: C.bg }}><th className="text-left py-1.5 px-2" style={{ color: C.slate }}>Class</th><th className="text-right py-1.5 px-2" style={{ color: C.slate }}>Policies</th><th className="text-right py-1.5 px-2" style={{ color: C.slate }}>GWP</th></tr></thead>
                <tbody>
                  {f.classes?.map((cl) => (
                    <tr key={cl.insurance_class} className="border-t border-[rgba(136,150,168,0.08)]">
                      <td className="py-1.5 px-2" style={{ color: C.navy }}>{cl.insurance_class}</td>
                      <td className="py-1.5 px-2 text-right font-mono" style={{ color: C.navy }}>{cl.policy_count}</td>
                      <td className="py-1.5 px-2 text-right font-mono" style={{ color: C.navy }}>{fmt$(cl.gross_premium)}</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </details>
        </Card>
      ))}
    </div>
  );
}

// ─── Module 05: Privacy ───
function SarWorkflow({ api, dsars, reload }) {
  const [showForm, setShowForm] = useState(false);
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [q, setQ] = useState('');
  const [hits, setHits] = useState([]);
  const [entity, setEntity] = useState(null);
  const [saving, setSaving] = useState(false);

  const search = (e) => {
    const val = e.target.value;
    setQ(val);
    clearTimeout(window._sarTimeout);
    window._sarTimeout = setTimeout(async () => {
      if (val.length < 2) { setHits([]); return; }
      try { const { data } = await api.get(`/entities/search?q=${encodeURIComponent(val)}&limit=5`); setHits(data); } catch {}
    }, 300);
  };

  const create = async () => {
    setSaving(true);
    try {
      await api.post('/compliance/privacy/dsar', {
        requester_name: name, requester_email: email,
        entity_id: entity?.entity_id || '', entity_name: entity?.legal_name || '',
      });
      toast.success('SAR registered — 30-day APP 12 clock started');
      setShowForm(false); setName(''); setEmail(''); setEntity(null); setQ('');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to register SAR'); }
    setSaving(false);
  };

  const markSent = async (id) => {
    try {
      await api.post(`/compliance/privacy/dsar/${id}/status`, { status: 'sent' });
      toast.success('SAR marked as sent');
      reload();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Update failed'); }
  };

  const daysLeft = (d) => Math.ceil((new Date(d.deadline) - Date.now()) / 86400000);

  return (
    <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="sar-workflow-card">
      <div className="flex items-center justify-between mb-2">
        <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Access Requests (APP 12)</h3>
        <Button size="sm" variant="outline" className="rounded-sm h-7 text-xs" onClick={() => setShowForm(s => !s)} data-testid="sar-new-btn">
          {showForm ? 'Cancel' : '+ New SAR'}
        </Button>
      </div>
      {showForm && (
        <div className="p-3 mb-3 rounded-sm space-y-2" style={{ background: C.bg }} data-testid="sar-form">
          <Input value={name} onChange={e => setName(e.target.value)} placeholder="Requester name *" className="rounded-sm h-8 text-xs bg-white" data-testid="sar-requester-input" />
          <Input value={email} onChange={e => setEmail(e.target.value)} placeholder="Requester email" className="rounded-sm h-8 text-xs bg-white" data-testid="sar-email-input" />
          {entity ? (
            <div className="flex items-center justify-between px-2 py-1.5 rounded-sm bg-white text-xs">
              <span style={{ color: C.navy }}>Linked client: <b>{entity.legal_name}</b></span>
              <button onClick={() => setEntity(null)} className="text-[10px] hover:underline" style={{ color: C.blue }}>change</button>
            </div>
          ) : (
            <div className="relative">
              <Input value={q} onChange={search} placeholder="Link the client record (search name / ABN)..." className="rounded-sm h-8 text-xs bg-white" data-testid="sar-entity-search" />
              {hits.map(h => (
                <button key={h.entity_id} onClick={() => { setEntity(h); setHits([]); }} data-testid={`sar-entity-${h.entity_id}`}
                  className="w-full text-left px-2 py-1.5 text-xs bg-white border-t hover:bg-[#F5F7FA]" style={{ borderColor: 'rgba(136,150,168,0.12)', color: C.navy }}>
                  {h.legal_name} <span style={{ color: C.slate }}>{h.abn || h.nzbn}</span>
                </button>
              ))}
            </div>
          )}
          <Button size="sm" className="rounded-sm h-8 text-xs text-white" style={{ background: C.blue }}
            disabled={!name.trim() || saving} onClick={create} data-testid="sar-create-btn">
            {saving ? 'Registering…' : 'Register SAR'}
          </Button>
        </div>
      )}
      {dsars.length === 0 ? <p className="text-xs py-4 text-center" style={{ color: C.slate }}>No access requests received</p> :
        dsars.map(d => {
          const dl = daysLeft(d);
          const closed = ['sent', 'closed'].includes(d.status);
          return (
            <div key={d.dsar_id} className="flex flex-wrap items-center gap-2 py-2 border-b border-[rgba(136,150,168,0.08)]" data-testid={`sar-row-${d.dsar_id}`}>
              <div className="flex-1 min-w-[150px]">
                <span className="font-mono text-xs" style={{ color: C.blue }}>{d.reference}</span>
                <p className="text-xs" style={{ color: C.slate }}>{d.requester_name}{d.entity_name ? ` · ${d.entity_name}` : ''}</p>
              </div>
              {!closed && (
                <span className="px-1.5 py-0.5 rounded text-[10px] font-bold" data-testid={`sar-countdown-${d.dsar_id}`}
                  style={{ background: (dl < 0 ? C.red : dl <= 7 ? C.amber : C.green) + '18', color: dl < 0 ? C.red : dl <= 7 ? '#B27516' : C.green }}>
                  {dl < 0 ? `${-dl}d OVERDUE` : `${dl}d left`}
                </span>
              )}
              <StatusDot status={d.status} />
              <a href={`${process.env.REACT_APP_BACKEND_URL}/api/compliance/privacy/dsar/${d.dsar_id}/pack`}
                target="_blank" rel="noopener noreferrer" data-testid={`sar-pack-${d.dsar_id}`}
                className="px-2 py-1 rounded-sm text-[10px] font-semibold text-white hover:opacity-90" style={{ background: '#7C5CBF' }}
                title="Download the APP 12 response pack (ZIP)">Response Pack</a>
              {d.data_extract_generated && !closed && (
                <button onClick={() => markSent(d.dsar_id)} data-testid={`sar-sent-${d.dsar_id}`}
                  className="px-2 py-1 rounded-sm text-[10px] font-semibold hover:opacity-80 border"
                  style={{ color: C.green, borderColor: 'rgba(29,158,117,0.4)' }}>Mark Sent</button>
              )}
            </div>
          );
        })
      }
    </Card>
  );
}

function PrivacyModule() {
  const api = useApi();
  const [dashboard, setDashboard] = useState(null);
  const [breaches, setBreaches] = useState([]);
  const [dsars, setDsars] = useState([]);
  const [loading, setLoading] = useState(true);

  const reload = () => Promise.all([
    api.get('/compliance/privacy/dashboard'),
    api.get('/compliance/privacy/breaches'),
    api.get('/compliance/privacy/dsars'),
  ]).then(([d, b, s]) => { setDashboard(d.data); setBreaches(b.data); setDsars(s.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { reload(); }, [api]);

  if (loading) return <Skeleton className="h-40 rounded-sm" />;

  return (
    <div className="space-y-4" data-testid="privacy-module">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniKPI label="Data Breaches" value={dashboard.total_breaches} color={dashboard.active_breaches > 0 ? C.red : C.green} />
          <MiniKPI label="Active" value={dashboard.active_breaches} color={C.amber} />
          <MiniKPI label="DSARs" value={dashboard.total_dsars} color={C.blue} />
          <MiniKPI label="Pending DSARs" value={dashboard.pending_dsars} color={C.amber} />
        </div>
      )}
      <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <h3 className="text-sm font-semibold mb-2" style={{ color: C.navy }}>Data Breaches</h3>
          {breaches.length === 0 ? <p className="text-xs py-4 text-center" style={{ color: C.slate }}>No data breaches recorded</p> :
            breaches.map(b => (
              <div key={b.breach_id} className="flex items-center justify-between py-2 border-b border-[rgba(136,150,168,0.08)]">
                <div><span className="font-mono text-xs" style={{ color: C.blue }}>{b.reference}</span><p className="text-xs" style={{ color: C.slate }}>{b.nature}</p></div>
                <StatusDot status={b.status} />
              </div>
            ))
          }
        </Card>
        <SarWorkflow api={api} dsars={dsars} reload={reload} />
      </div>
    </div>
  );
}

// ─── Module 06: Trust Account ───
function TrustAccountModule() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/compliance/trust-account').then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <Skeleton className="h-40 rounded-sm" />;

  return (
    <div className="space-y-4" data-testid="trust-account-module">
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKPI label="Total Premium" value={fmt$(data?.total_premium_written || 0)} color={C.blue} />
        <MiniKPI label="Receipts" value={fmt$(data?.total_receipts || 0)} color={C.green} />
        <MiniKPI label="Disbursements" value={fmt$(data?.total_disbursements || 0)} color={C.amber} />
        <MiniKPI label="Balance" value={fmt$(data?.balance || 0)} color={C.teal} />
      </div>
      <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>Stamp Duty Rates by State</h3>
        <div className="grid grid-cols-3 md:grid-cols-5 gap-3">
          {Object.entries(data?.stamp_duty_rates || {}).map(([state, rate]) => (
            <div key={state} className="text-center p-2 rounded-sm" style={{ background: C.bg }}>
              <p className="text-xs font-semibold" style={{ color: C.navy }}>{state}</p>
              <p className="text-sm font-mono" style={{ color: C.blue }}>{rate === 0 ? 'N/A' : `${(rate * 100).toFixed(0)}%`}</p>
            </div>
          ))}
        </div>
      </Card>
      <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="flex items-center justify-between">
          <div>
            <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Reconciliation Status</h3>
            <p className="text-xs mt-0.5" style={{ color: C.slate }}>Last reconciliation: {data?.last_reconciliation ? new Date(data.last_reconciliation).toLocaleString() : '—'}</p>
          </div>
          <span className="flex items-center gap-1 px-2 py-1 rounded text-xs font-medium" style={{ background: C.green + '15', color: C.green }}>
            <CheckCircle2 className="w-3.5 h-3.5" /> Current
          </span>
        </div>
      </Card>
    </div>
  );
}

// ─── Module 07: Binding Authority ───
function BindingAuthorityModule() {
  const api = useApi();
  const [authorities, setAuthorities] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ capacity_provider: '', authority_reference: '', classes_authorised: [], aggregate_gwp_limit: 0, max_si_per_risk: 0, effective_date: '', expiry_date: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    api.get('/compliance/authorities').then(r => setAuthorities(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    await api.post('/compliance/authorities', form);
    setShowCreate(false);
    load();
  };

  if (loading) return <Skeleton className="h-40 rounded-sm" />;

  return (
    <div className="space-y-4" data-testid="binding-authority-module">
      <div className="flex gap-2">
        <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.blue }} data-testid="create-authority-btn">
          <Plus className="w-4 h-4" /> Add Authority
        </button>
      </div>

      {showCreate && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3">
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>New Binding Authority</h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Capacity Provider" value={form.capacity_provider} onChange={e => setForm({...form, capacity_provider: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" data-testid="authority-provider-input" />
            <input placeholder="Reference Number" value={form.authority_reference} onChange={e => setForm({...form, authority_reference: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
            <input type="number" placeholder="GWP Limit" value={form.aggregate_gwp_limit || ''} onChange={e => setForm({...form, aggregate_gwp_limit: parseFloat(e.target.value) || 0})} className="px-3 py-2 text-sm border rounded-sm" />
            <input type="number" placeholder="Max SI per Risk" value={form.max_si_per_risk || ''} onChange={e => setForm({...form, max_si_per_risk: parseFloat(e.target.value) || 0})} className="px-3 py-2 text-sm border rounded-sm" />
            <input type="date" placeholder="Effective Date" value={form.effective_date} onChange={e => setForm({...form, effective_date: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
            <input type="date" placeholder="Expiry Date" value={form.expiry_date} onChange={e => setForm({...form, expiry_date: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-4 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.green }}>Create</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-sm text-sm font-medium" style={{ color: C.slate }}>Cancel</button>
          </div>
        </Card>
      )}

      {authorities.length === 0 ? (
        <Card className="p-8 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white text-center">
          <Building2 className="w-8 h-8 mx-auto mb-2 opacity-40" style={{ color: C.slate }} />
          <p className="text-sm" style={{ color: C.slate }}>No binding authorities registered</p>
        </Card>
      ) : authorities.map(a => <AuthorityCard key={a.authority_id} a={a} onChanged={load} />)}
    </div>
  );
}

// One binder on the register: its terms, the live book read against them, and the edit panel.
function AuthorityCard({ a, onChanged }) {
  const api = useApi();
  const [editing, setEditing] = useState(false);
  const [book, setBook] = useState(null);
  const [history, setHistory] = useState([]);
  const isExpired = a.expiry_date && new Date(a.expiry_date) < new Date();
  const perClass = Object.entries(a.per_risk_limits || {});
  const loadBook = useCallback(() => {
    api.get(`/compliance/authorities/${a.authority_id}/book`).then(r => setBook(r.data)).catch(() => {});
    api.get(`/compliance/authorities/${a.authority_id}/history`).then(r => setHistory(r.data)).catch(() => {});
  }, [api, a.authority_id]);
  useEffect(() => { loadBook(); }, [loadBook]);
  return (
    <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid={`authority-card-${a.authority_id}`}>
      <div className="flex items-center justify-between mb-2">
        <div>
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>{a.capacity_provider}</h3>
          <p className="text-xs font-mono" style={{ color: C.slate }}>{a.authority_reference}</p>
        </div>
        <div className="flex items-center gap-2">
          <span className="px-2 py-0.5 rounded text-[11px] font-medium uppercase" style={{
            background: (isExpired ? C.red : C.green) + '15',
            color: isExpired ? C.red : C.green
          }}>{isExpired ? 'Expired' : a.status}</span>
          <button onClick={() => setEditing(v => !v)} className="px-2.5 py-1 rounded-sm text-[11px] font-medium border" style={{ color: C.blue, borderColor: 'rgba(26,76,124,0.3)' }} data-testid={`authority-edit-${a.authority_id}`}>
            {editing ? 'Close' : 'Edit terms'}
          </button>
        </div>
      </div>
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
        <div><p style={{ color: C.slate }}>GWP Limit</p><p className="font-mono font-medium" style={{ color: C.navy }}>{fmt$(a.aggregate_gwp_limit)}</p></div>
        <div><p style={{ color: C.slate }}>Per-risk (binder-wide)</p><p className="font-mono font-medium" style={{ color: C.navy }} data-testid={`authority-max-si-${a.authority_id}`}>{fmt$(a.max_si_per_risk)}</p></div>
        <div><p style={{ color: C.slate }}>Effective</p><p className="font-medium" style={{ color: C.navy }}>{a.effective_date || '—'}</p></div>
        <div><p style={{ color: C.slate }}>Expiry</p><p className="font-medium" style={{ color: isExpired ? C.red : C.navy }}>{a.expiry_date || '—'}</p></div>
      </div>
      {a.classes_authorised?.length > 0 && (
        <div className="mt-2 flex flex-wrap gap-1">
          {a.classes_authorised.map(cl => {
            const line = (a.per_risk_limits || {})[cl];
            return (
              <span key={cl} className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: C.blue + '10', color: C.blue }} data-testid={`authority-class-${a.authority_id}-${cl.replace(/[^a-z0-9]+/gi, '-').toLowerCase()}`}>
                {cl}{line ? <span className="font-mono"> · {fmt$(line)}</span> : null}
              </span>
            );
          })}
          {perClass.length === 0 && <span className="text-[10px]" style={{ color: C.slate }}>no per-class lines — the binder-wide figure applies to every class</span>}
        </div>
      )}
      <BinderBookHint book={book} history={history} />
      {editing && <BinderTermsEditor authority={a} book={book} onSaved={() => { setEditing(false); onChanged(); loadBook(); }} onCancel={() => setEditing(false)} />}
    </Card>
  );
}

// ─── Module 08: Policy Wordings ───
function WordingsModule() {
  const api = useApi();
  const [dashboard, setDashboard] = useState(null);
  const [wordings, setWordings] = useState([]);
  const [templates, setTemplates] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ insurance_class: '', version: '1.0', insurer_name: '', plain_english_summary: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    Promise.all([
      api.get('/compliance/wordings/dashboard'),
      api.get('/compliance/wordings'),
      api.get('/compliance/endorsement-templates'),
    ]).then(([d, w, t]) => { setDashboard(d.data); setWordings(w.data); setTemplates(t.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    await api.post('/compliance/wordings', form);
    setShowCreate(false);
    setForm({ insurance_class: '', version: '1.0', insurer_name: '', plain_english_summary: '' });
    load();
  };

  const downloadWording = async (w) => {
    const { data } = await api.get(`/compliance/wordings/${w.wording_id}/document`, { responseType: 'blob' });
    const url = URL.createObjectURL(data);
    const a = document.createElement('a');
    a.href = url; a.download = w.document_file || `${w.insurance_class}_wording.docx`;
    a.click();
    URL.revokeObjectURL(url);
  };

  if (loading) return <Skeleton className="h-40 rounded-sm" />;
  return (
    <div className="space-y-4" data-testid="wordings-module">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <MiniKPI label="Active Wordings" value={dashboard.active_wordings} color={C.blue} />
          <MiniKPI label="Endorsement Templates" value={dashboard.endorsement_templates} color={C.teal} />
          <MiniKPI label="Classes Covered" value={dashboard.by_class?.length || 0} color={C.green} />
        </div>
      )}
      {dashboard && dashboard.coverage && (
        <Card className="p-3.5 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="wording-coverage-report">
          <p className="text-xs font-semibold uppercase tracking-wider" style={{ color: C.navy }}>
            Wording coverage — {dashboard.coverage.classes_covered}/{dashboard.coverage.classes_total} classes
          </p>
          {dashboard.coverage.missing.length === 0 ? (
            <p className="text-xs mt-1.5" style={{ color: C.green }}>All active classes have a wording on file.</p>
          ) : (
            <div className="flex flex-wrap gap-1.5 mt-2">
              {dashboard.coverage.missing.map(m => (
                <span key={m.id} data-testid={`missing-chip-${m.id}`} className="text-[11px] px-2 py-0.5 rounded-full" style={{ background: 'rgba(232,160,32,0.12)', color: '#A8720E', border: '1px solid rgba(232,160,32,0.35)' }}>
                  {m.name} — missing
                </span>
              ))}
            </div>
          )}
        </Card>
      )}
      <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.blue }} data-testid="add-wording-btn">
        <Plus className="w-4 h-4" /> Add Wording
      </button>
      {showCreate && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Insurance Class" value={form.insurance_class} onChange={e => setForm({...form, insurance_class: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
            <input placeholder="Insurer Name" value={form.insurer_name} onChange={e => setForm({...form, insurer_name: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
            <input placeholder="Version" value={form.version} onChange={e => setForm({...form, version: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
          </div>
          <textarea placeholder="Plain English Summary" value={form.plain_english_summary} onChange={e => setForm({...form, plain_english_summary: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-sm h-16" />
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-4 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.green }}>Save</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-sm text-sm" style={{ color: C.slate }}>Cancel</button>
          </div>
        </Card>
      )}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]"><h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Wording Library</h3></div>
        <div className="p-4">
          {wordings.length === 0 ? <p className="text-sm text-center py-4" style={{ color: C.slate }}>No wordings registered. Add insurer-approved wordings for each class.</p> :
            wordings.map(w => (
              <div key={w.wording_id} className="flex items-center justify-between py-2 border-b border-[rgba(136,150,168,0.08)]">
                <div className="min-w-0">
                  <span className="text-xs font-medium" style={{ color: C.navy }}>{w.insurance_class}</span>
                  <span className="text-xs ml-2" style={{ color: C.slate }}>v{w.version} — {w.insurer_name}</span>
                  {w.plain_english_summary && <p className="text-[11px] mt-0.5 truncate max-w-xl" style={{ color: C.slate }}>{w.plain_english_summary}</p>}
                </div>
                <div className="flex items-center gap-3 shrink-0">
                  {w.public_url && (
                    <button onClick={() => { navigator.clipboard.writeText(w.public_url); toast.success('Public wording link copied'); }}
                      className="flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: C.teal }}
                      data-testid={`copy-wording-link-${w.insurance_class}`} title={w.public_url}>
                      <Copy className="w-3.5 h-3.5" /> Link
                    </button>
                  )}
                  {w.document_file && (
                    <button onClick={() => downloadWording(w)} className="flex items-center gap-1 text-xs font-medium hover:underline" style={{ color: C.blue }} data-testid={`download-wording-${w.insurance_class}`}>
                      <Download className="w-3.5 h-3.5" /> PDS
                    </button>
                  )}
                  <StatusDot status={w.status} />
                </div>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Module 09: Reinsurance ───
function ReinsuranceModule() {
  const api = useApi();
  const [dashboard, setDashboard] = useState(null);
  const [treaties, setTreaties] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ reinsurer_name: '', treaty_type: 'quota_share', cedant_percentage: 0, limit: 0 });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    Promise.all([
      api.get('/compliance/treaties/dashboard'),
      api.get('/compliance/treaties'),
    ]).then(([d, t]) => { setDashboard(d.data); setTreaties(t.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    await api.post('/compliance/treaties', form);
    setShowCreate(false);
    load();
  };

  if (loading) return <Skeleton className="h-40 rounded-sm" />;
  return (
    <div className="space-y-4" data-testid="reinsurance-module">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniKPI label="Active Treaties" value={dashboard.active_treaties} color={C.blue} />
          <MiniKPI label="Total Treaties" value={dashboard.total_treaties} color={C.slate} />
          <MiniKPI label="Ceded Premium" value={fmt$(dashboard.total_ceded_premium)} color={C.amber} />
          <MiniKPI label="Recoveries" value={fmt$(dashboard.total_recoveries)} color={C.green} />
        </div>
      )}
      <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.blue }} data-testid="add-treaty-btn">
        <Plus className="w-4 h-4" /> Add Treaty
      </button>
      {showCreate && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Reinsurer Name" value={form.reinsurer_name} onChange={e => setForm({...form, reinsurer_name: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
            <select value={form.treaty_type} onChange={e => setForm({...form, treaty_type: e.target.value})} className="px-3 py-2 text-sm border rounded-sm">
              <option value="quota_share">Quota Share</option><option value="excess_of_loss">Excess of Loss</option>
              <option value="facultative">Facultative</option><option value="stop_loss">Stop Loss</option>
            </select>
            <input type="number" placeholder="Cedant %" value={form.cedant_percentage || ''} onChange={e => setForm({...form, cedant_percentage: parseFloat(e.target.value) || 0})} className="px-3 py-2 text-sm border rounded-sm" />
            <input type="number" placeholder="Limit ($)" value={form.limit || ''} onChange={e => setForm({...form, limit: parseFloat(e.target.value) || 0})} className="px-3 py-2 text-sm border rounded-sm" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-4 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.green }}>Create</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-sm text-sm" style={{ color: C.slate }}>Cancel</button>
          </div>
        </Card>
      )}
      {treaties.length === 0 ? (
        <Card className="p-8 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white text-center">
          <p className="text-sm" style={{ color: C.slate }}>No reinsurance treaties registered</p>
        </Card>
      ) : treaties.map(t => (
        <Card key={t.treaty_id} className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <div className="flex items-center justify-between mb-2">
            <div><h3 className="text-sm font-semibold" style={{ color: C.navy }}>{t.reinsurer_name}</h3><p className="text-xs capitalize" style={{ color: C.slate }}>{t.treaty_type.replace(/_/g, ' ')} — {t.treaty_reference}</p></div>
            <StatusDot status={t.status} />
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-3 text-xs">
            <div><p style={{ color: C.slate }}>Cedant %</p><p className="font-mono font-medium" style={{ color: C.navy }}>{t.cedant_percentage}%</p></div>
            <div><p style={{ color: C.slate }}>Limit</p><p className="font-mono font-medium" style={{ color: C.navy }}>{fmt$(t.limit)}</p></div>
            <div><p style={{ color: C.slate }}>Retention</p><p className="font-mono font-medium" style={{ color: C.navy }}>{fmt$(t.retention_amount)}</p></div>
            <div><p style={{ color: C.slate }}>Expiry</p><p className="font-medium" style={{ color: C.navy }}>{t.expiry_date || '—'}</p></div>
          </div>
        </Card>
      ))}
    </div>
  );
}

// ─── Module 10: GI Code ───
function GICodeModule() {
  const api = useApi();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    api.get('/compliance/gi-code').then(r => setData(r.data)).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);

  if (loading) return <Skeleton className="h-40 rounded-sm" />;
  return (
    <div className="space-y-4" data-testid="gi-code-module">
      <div className="grid grid-cols-2 md:grid-cols-3 gap-3">
        <MiniKPI label="Total Claims" value={data?.total_claims || 0} color={C.blue} />
        <MiniKPI label="Hardship Cases" value={data?.hardship_cases || 0} color={C.amber} />
        <MiniKPI label="Vulnerability Flags" value={data?.vulnerability_flags || 0} color={C.red} />
      </div>
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]"><h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>GI Code Requirements</h3></div>
        <div className="divide-y divide-[rgba(136,150,168,0.08)]">
          {data?.requirements?.map(req => {
            const status = data.compliance_status?.[req.id] || {};
            return (
              <div key={req.id} className="flex items-center justify-between p-3">
                <div className="flex-1">
                  <p className="text-sm font-medium" style={{ color: C.navy }}>{req.name}</p>
                  <p className="text-xs" style={{ color: C.slate }}>{req.description}</p>
                  <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>Timeframe: {req.timeframe}</p>
                </div>
                <div className="text-right ml-4">
                  <span className="px-2 py-0.5 rounded text-[10px] font-medium" style={{ background: (status.compliant ? C.green : C.red) + '15', color: status.compliant ? C.green : C.red }}>
                    {status.compliant ? 'Compliant' : 'Action'}
                  </span>
                  <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>{status.detail}</p>
                </div>
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

// ─── Module 11: Staff Competency ───
function StaffModule() {
  const api = useApi();
  const [dashboard, setDashboard] = useState(null);
  const [staff, setStaff] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ name: '', role: '', email: '', rg146_tier: '', responsible_manager: false });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    Promise.all([
      api.get('/compliance/staff/dashboard'),
      api.get('/compliance/staff'),
    ]).then(([d, s]) => { setDashboard(d.data); setStaff(s.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    await api.post('/compliance/staff', form);
    setShowCreate(false);
    setForm({ name: '', role: '', email: '', rg146_tier: '', responsible_manager: false });
    load();
  };

  if (loading) return <Skeleton className="h-40 rounded-sm" />;
  return (
    <div className="space-y-4" data-testid="staff-module">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniKPI label="Total Staff" value={dashboard.total_staff} color={C.blue} />
          <MiniKPI label="Resp. Managers" value={dashboard.responsible_managers} color={C.navy} />
          <MiniKPI label="Auth Reps" value={dashboard.authorised_reps} color={C.teal} />
          <MiniKPI label="Avg CPD Hours" value={dashboard.avg_cpd_hours} color={C.green} />
        </div>
      )}
      {dashboard && (dashboard.aml_training_overdue > 0 || dashboard.gi_code_training_overdue > 0) && (
        <Card className="p-3 rounded-sm border border-[rgba(232,160,32,0.3)] bg-[rgba(232,160,32,0.05)]">
          <div className="flex items-center gap-2">
            <AlertTriangle className="w-4 h-4" style={{ color: C.amber }} />
            <p className="text-xs font-medium" style={{ color: C.amber }}>
              {dashboard.aml_training_overdue} AML training overdue, {dashboard.gi_code_training_overdue} GI Code training overdue
            </p>
          </div>
        </Card>
      )}
      <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.blue }} data-testid="add-staff-btn">
        <Plus className="w-4 h-4" /> Add Staff
      </button>
      {showCreate && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-3">
            <input placeholder="Full Name" value={form.name} onChange={e => setForm({...form, name: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" data-testid="staff-name-input" />
            <select value={form.role} onChange={e => setForm({...form, role: e.target.value})} className="px-3 py-2 text-sm border rounded-sm">
              <option value="">Select Role</option><option value="uw">Underwriter</option><option value="claims">Claims</option>
              <option value="compliance">Compliance</option><option value="finance">Finance</option><option value="admin">Admin</option>
            </select>
            <input placeholder="Email" value={form.email} onChange={e => setForm({...form, email: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
          </div>
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-4 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.green }}>Add Staff</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-sm text-sm" style={{ color: C.slate }}>Cancel</button>
          </div>
        </Card>
      )}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]"><h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Staff Register</h3></div>
        <div className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead>
              <tr style={{ background: C.bg }} className="border-b border-[rgba(136,150,168,0.15)]">
                {['Name', 'Role', 'RG146', 'CPD Hours', 'AML Training', 'GI Code', 'RM', 'Status'].map(h => (
                  <th key={h} className="text-left py-2 px-3 text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                ))}
              </tr>
            </thead>
            <tbody>
              {staff.length === 0 ? (
                <tr><td colSpan={8} className="py-6 text-center text-sm" style={{ color: C.slate }}>No staff registered</td></tr>
              ) : staff.map(s => (
                <tr key={s.staff_id} className="border-b border-[rgba(136,150,168,0.08)]">
                  <td className="py-2 px-3 text-xs font-medium" style={{ color: C.navy }}>{s.name}</td>
                  <td className="py-2 px-3 text-xs uppercase" style={{ color: C.slate }}>{s.role}</td>
                  <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{s.rg146_tier || '—'}</td>
                  <td className="py-2 px-3 text-xs font-mono" style={{ color: s.cpd_hours_current_year >= 30 ? C.green : C.amber }}>{s.cpd_hours_current_year}h</td>
                  <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{s.aml_training_date ? new Date(s.aml_training_date).toLocaleDateString('en-AU') : 'Overdue'}</td>
                  <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{s.gi_code_training_date ? new Date(s.gi_code_training_date).toLocaleDateString('en-AU') : 'Overdue'}</td>
                  <td className="py-2 px-3">{s.responsible_manager ? <CheckCircle2 className="w-3.5 h-3.5" style={{ color: C.green }} /> : <span className="text-xs" style={{ color: C.slate }}>—</span>}</td>
                  <td className="py-2 px-3"><StatusDot status={s.status} /></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </Card>
    </div>
  );
}

// ─── Module 12: Conflicts of Interest ───
function ConflictsModule() {
  const api = useApi();
  const [dashboard, setDashboard] = useState(null);
  const [conflicts, setConflicts] = useState([]);
  const [gifts, setGifts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ nature: '', parties_involved: [], conflict_type: 'potential', management_plan: '', disclosure_required: false });
  const [tab, setTab] = useState('conflicts');

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    Promise.all([
      api.get('/compliance/conflicts/dashboard'),
      api.get('/compliance/conflicts'),
      api.get('/compliance/gifts'),
    ]).then(([d, c, g]) => { setDashboard(d.data); setConflicts(c.data); setGifts(g.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    const data = { ...form, parties_involved: form.parties_involved_str ? form.parties_involved_str.split(',').map(s => s.trim()) : [] };
    await api.post('/compliance/conflicts', data);
    setShowCreate(false);
    load();
  };

  if (loading) return <Skeleton className="h-40 rounded-sm" />;
  return (
    <div className="space-y-4" data-testid="conflicts-module">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniKPI label="Active Conflicts" value={dashboard.active} color={C.blue} />
          <MiniKPI label="Overdue Reviews" value={dashboard.overdue_reviews} color={dashboard.overdue_reviews > 0 ? C.red : C.green} />
          <MiniKPI label="Gifts Flagged" value={dashboard.gifts_flagged} color={dashboard.gifts_flagged > 0 ? C.amber : C.green} />
          <MiniKPI label="RPT Pending" value={dashboard.related_party_pending_signoff} color={C.amber} />
        </div>
      )}

      {/* Tabs */}
      <div className="flex gap-1 border-b border-[rgba(136,150,168,0.2)]">
        {[{id: 'conflicts', label: `Conflicts (${conflicts.length})`}, {id: 'gifts', label: `Gifts (${gifts.length})`}].map(t => (
          <button key={t.id} onClick={() => setTab(t.id)}
            className={`px-4 py-2 text-sm font-medium border-b-2 transition-colors ${tab === t.id ? 'border-[#1A4C7C]' : 'border-transparent hover:border-[rgba(136,150,168,0.3)]'}`}
            style={{ color: tab === t.id ? C.navy : C.slate }}>{t.label}</button>
        ))}
      </div>

      {tab === 'conflicts' && (
        <>
          <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.blue }} data-testid="add-conflict-btn">
            <Plus className="w-4 h-4" /> Log Conflict
          </button>
          {showCreate && (
            <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3">
              <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                <input placeholder="Nature of conflict" value={form.nature} onChange={e => setForm({...form, nature: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" data-testid="conflict-nature-input" />
                <select value={form.conflict_type} onChange={e => setForm({...form, conflict_type: e.target.value})} className="px-3 py-2 text-sm border rounded-sm">
                  <option value="actual">Actual</option><option value="perceived">Perceived</option><option value="potential">Potential</option>
                </select>
                <input placeholder="Parties (comma separated)" value={form.parties_involved_str || ''} onChange={e => setForm({...form, parties_involved_str: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
              </div>
              <textarea placeholder="Management Plan" value={form.management_plan} onChange={e => setForm({...form, management_plan: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-sm h-16" />
              <div className="flex gap-2">
                <button onClick={handleCreate} className="px-4 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.green }}>Submit</button>
                <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-sm text-sm" style={{ color: C.slate }}>Cancel</button>
              </div>
            </Card>
          )}
          <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
            <div className="divide-y divide-[rgba(136,150,168,0.08)]">
              {conflicts.length === 0 ? <p className="p-6 text-center text-sm" style={{ color: C.slate }}>No conflicts registered</p> :
                conflicts.map(c => (
                  <div key={c.conflict_id} className="p-3">
                    <div className="flex items-center justify-between mb-1">
                      <div className="flex items-center gap-2">
                        <span className="font-mono text-xs" style={{ color: C.blue }}>{c.reference}</span>
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium capitalize" style={{ background: (c.conflict_type === 'actual' ? C.red : c.conflict_type === 'perceived' ? C.amber : C.teal) + '15', color: c.conflict_type === 'actual' ? C.red : c.conflict_type === 'perceived' ? C.amber : C.teal }}>{c.conflict_type}</span>
                      </div>
                      <StatusDot status={c.status} />
                    </div>
                    <p className="text-xs" style={{ color: C.navy }}>{c.nature}</p>
                    {c.parties_involved?.length > 0 && <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>Parties: {c.parties_involved.join(', ')}</p>}
                    {c.management_plan && <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>Plan: {c.management_plan}</p>}
                    <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>Review: {c.review_date || '—'} | Disclosure: {c.disclosure_required ? 'Required' : 'No'}</p>
                  </div>
                ))}
            </div>
          </Card>
        </>
      )}

      {tab === 'gifts' && (
        <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <div className="p-3 border-b border-[rgba(136,150,168,0.15)]"><h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Gift & Hospitality Register</h3></div>
          <div className="divide-y divide-[rgba(136,150,168,0.08)]">
            {gifts.length === 0 ? <p className="p-6 text-center text-sm" style={{ color: C.slate }}>No gifts logged</p> :
              gifts.map(g => (
                <div key={g.gift_id} className="p-3 flex items-center justify-between">
                  <div>
                    <p className="text-xs font-medium" style={{ color: C.navy }}>{g.recipient_name} from {g.donor_name}</p>
                    <p className="text-[10px]" style={{ color: C.slate }}>{g.description} — {g.date_received}</p>
                  </div>
                  <div className="text-right">
                    <p className="text-sm font-mono font-medium" style={{ color: g.value > 150 ? C.red : C.navy }}>${g.value}</p>
                    <StatusDot status={g.status} />
                  </div>
                </div>
              ))}
          </div>
        </Card>
      )}
    </div>
  );
}

// ─── Module 13: DDO / TMD ───
function DDOModule() {
  const api = useApi();
  const [dashboard, setDashboard] = useState(null);
  const [tmds, setTmds] = useState([]);
  const [pdsList, setPdsList] = useState([]);
  const [report, setReport] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ product_name: '', insurance_class: '', target_market_description: '', insurer_name: '' });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    Promise.all([
      api.get('/compliance/ddo/dashboard'),
      api.get('/compliance/ddo/tmds'),
      api.get('/compliance/ddo/pds'),
      api.get('/compliance/ddo/quarterly-report'),
    ]).then(([d, t, p, r]) => { setDashboard(d.data); setTmds(t.data); setPdsList(p.data); setReport(r.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    await api.post('/compliance/ddo/tmd', form);
    setShowCreate(false);
    setForm({ product_name: '', insurance_class: '', target_market_description: '', insurer_name: '' });
    load();
  };

  if (loading) return <Skeleton className="h-40 rounded-sm" />;
  return (
    <div className="space-y-4" data-testid="ddo-module">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniKPI label="Active TMDs" value={dashboard.active_tmds} color={C.blue} />
          <MiniKPI label="PDS Documents" value={dashboard.pds_count} color={C.teal} />
          <MiniKPI label="Overdue Reviews" value={dashboard.overdue_reviews} color={dashboard.overdue_reviews > 0 ? C.red : C.green} />
          <MiniKPI label="Total TMDs" value={dashboard.total_tmds} color={C.slate} />
        </div>
      )}

      {/* Quarterly Report */}
      {report && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <h3 className="text-sm font-semibold mb-2" style={{ color: C.navy }}>DDO Distribution Report — {report.period}</h3>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-4 text-center">
            <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Total Distributed</p><p className="text-lg font-semibold" style={{ color: C.navy }}>{report.total_distributed?.toLocaleString()}</p></div>
            <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>In Target</p><p className="text-lg font-semibold" style={{ color: C.green }}>{report.in_target_market?.toLocaleString()}</p></div>
            <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Out of Target</p><p className="text-lg font-semibold" style={{ color: report.out_of_target_market > 0 ? C.red : C.green }}>{report.out_of_target_market}</p></div>
            <div><p className="text-[10px] uppercase" style={{ color: C.slate }}>Significant Dealings</p><p className="text-lg font-semibold" style={{ color: report.significant_dealings ? C.red : C.green }}>{report.significant_dealings ? 'Yes' : 'No'}</p></div>
          </div>
        </Card>
      )}

      <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.blue }} data-testid="add-tmd-btn">
        <Plus className="w-4 h-4" /> Add TMD
      </button>
      {showCreate && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <input placeholder="Product Name" value={form.product_name} onChange={e => setForm({...form, product_name: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" data-testid="tmd-product-input" />
            <input placeholder="Insurance Class" value={form.insurance_class} onChange={e => setForm({...form, insurance_class: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
            <input placeholder="Insurer Name" value={form.insurer_name} onChange={e => setForm({...form, insurer_name: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" />
          </div>
          <textarea placeholder="Target Market Description" value={form.target_market_description} onChange={e => setForm({...form, target_market_description: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-sm h-16" />
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-4 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.green }}>Create TMD</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-sm text-sm" style={{ color: C.slate }}>Cancel</button>
          </div>
        </Card>
      )}

      {/* TMD List */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]"><h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Target Market Determinations</h3></div>
        <div className="divide-y divide-[rgba(136,150,168,0.08)]">
          {tmds.length === 0 ? <p className="p-6 text-center text-sm" style={{ color: C.slate }}>No TMDs registered. Required for personal lines products.</p> :
            tmds.map(t => (
              <div key={t.tmd_id} className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <span className="text-xs font-medium" style={{ color: C.navy }}>{t.product_name} — {t.insurance_class}</span>
                  <StatusDot status={t.status} />
                </div>
                <p className="text-[10px]" style={{ color: C.slate }}>{t.target_market_description || 'No description set'}</p>
                <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>Distribution: {t.distribution_conditions} | Review: every {t.review_period_months} months</p>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Module 14: BCP / DR ───
function BCPModule() {
  const api = useApi();
  const [dashboard, setDashboard] = useState(null);
  const [incidents, setIncidents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [form, setForm] = useState({ nature: '', description: '', impact: 'minor', services_affected: [] });

  // eslint-disable-next-line react-hooks/exhaustive-deps
  const load = useCallback(() => {
    Promise.all([
      api.get('/compliance/bcp/dashboard'),
      api.get('/compliance/bcp/incidents'),
    ]).then(([d, i]) => { setDashboard(d.data); setIncidents(i.data); }).catch(err => { console.error('API error:', err?.message); }).finally(() => setLoading(false));
  }, [api]);
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { load(); }, [load]);

  const handleCreate = async () => {
    await api.post('/compliance/bcp/incidents', form);
    setShowCreate(false);
    setForm({ nature: '', description: '', impact: 'minor', services_affected: [] });
    load();
  };

  if (loading) return <Skeleton className="h-40 rounded-sm" />;

  const impactColor = { none: C.green, minor: C.teal, moderate: C.amber, major: C.red, critical: C.red };

  return (
    <div className="space-y-4" data-testid="bcp-module">
      {dashboard && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          <MiniKPI label="Total Incidents" value={dashboard.total_incidents} color={C.blue} />
          <MiniKPI label="Active" value={dashboard.active_incidents} color={dashboard.active_incidents > 0 ? C.red : C.green} />
          <MiniKPI label="Resolved" value={dashboard.resolved_incidents} color={C.green} />
          <MiniKPI label="Avg Resolution" value={`${dashboard.avg_resolution_minutes}m`} color={C.teal} />
        </div>
      )}

      {/* BCP Targets */}
      {dashboard && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>Recovery Targets</h3>
          <div className="grid grid-cols-3 gap-4 text-center">
            <div className="p-2 rounded-sm" style={{ background: C.bg }}>
              <p className="text-[10px] uppercase" style={{ color: C.slate }}>RPO</p>
              <p className="text-sm font-semibold" style={{ color: C.navy }}>{dashboard.bcp_targets?.rpo}</p>
            </div>
            <div className="p-2 rounded-sm" style={{ background: C.bg }}>
              <p className="text-[10px] uppercase" style={{ color: C.slate }}>RTO Production</p>
              <p className="text-sm font-semibold" style={{ color: C.navy }}>{dashboard.bcp_targets?.rto_production}</p>
            </div>
            <div className="p-2 rounded-sm" style={{ background: C.bg }}>
              <p className="text-[10px] uppercase" style={{ color: C.slate }}>RTO CAT Mode</p>
              <p className="text-sm font-semibold" style={{ color: C.navy }}>{dashboard.bcp_targets?.rto_cat_mode}</p>
            </div>
          </div>
        </Card>
      )}

      {/* Supplier Dependencies */}
      {dashboard?.supplier_dependencies && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
          <h3 className="text-sm font-semibold mb-3" style={{ color: C.navy }}>Supplier Dependencies</h3>
          <div className="overflow-x-auto">
            <table className="w-full text-xs">
              <thead>
                <tr style={{ background: C.bg }}>
                  {['Service', 'Type', 'Criticality', 'Fallback'].map(h => (
                    <th key={h} className="text-left py-1.5 px-2 text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody>
                {dashboard.supplier_dependencies.map((s) => (
                  <tr key={s.name} className="border-t border-[rgba(136,150,168,0.08)]">
                    <td className="py-1.5 px-2 font-medium" style={{ color: C.navy }}>{s.name}</td>
                    <td className="py-1.5 px-2" style={{ color: C.slate }}>{s.type}</td>
                    <td className="py-1.5 px-2">
                      {(() => { const cc = {critical: C.red, high: C.amber, medium: C.teal, low: C.green}[s.criticality] || C.green; return (
                        <span className="px-1.5 py-0.5 rounded text-[10px] font-medium capitalize" style={{ background: cc + '15', color: cc }}>{s.criticality}</span>
                      ); })()}
                    </td>
                    <td className="py-1.5 px-2" style={{ color: C.slate }}>{s.fallback}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {/* Incident Log */}
      <button onClick={() => setShowCreate(!showCreate)} className="flex items-center gap-1.5 px-3 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.blue }} data-testid="log-incident-btn">
        <Plus className="w-4 h-4" /> Log Incident
      </button>
      {showCreate && (
        <Card className="p-4 rounded-sm border border-[rgba(136,150,168,0.2)] bg-white space-y-3">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
            <select value={form.nature} onChange={e => setForm({...form, nature: e.target.value})} className="px-3 py-2 text-sm border rounded-sm" data-testid="incident-nature-select">
              <option value="">Select Nature</option>
              <option value="system_outage">System Outage</option><option value="api_failure">API Failure</option>
              <option value="data_breach">Data Breach</option><option value="infrastructure">Infrastructure</option>
              <option value="cyber_attack">Cyber Attack</option>
            </select>
            <select value={form.impact} onChange={e => setForm({...form, impact: e.target.value})} className="px-3 py-2 text-sm border rounded-sm">
              <option value="none">None</option><option value="minor">Minor</option><option value="moderate">Moderate</option>
              <option value="major">Major</option><option value="critical">Critical</option>
            </select>
          </div>
          <textarea placeholder="Description" value={form.description} onChange={e => setForm({...form, description: e.target.value})} className="w-full px-3 py-2 text-sm border rounded-sm h-16" />
          <div className="flex gap-2">
            <button onClick={handleCreate} className="px-4 py-2 rounded-sm text-sm font-medium text-white" style={{ background: C.green }}>Log Incident</button>
            <button onClick={() => setShowCreate(false)} className="px-4 py-2 rounded-sm text-sm" style={{ color: C.slate }}>Cancel</button>
          </div>
        </Card>
      )}

      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]"><h3 className="text-sm font-semibold uppercase tracking-wider" style={{ color: C.navy }}>Incident Log</h3></div>
        <div className="divide-y divide-[rgba(136,150,168,0.08)]">
          {incidents.length === 0 ? <p className="p-6 text-center text-sm" style={{ color: C.slate }}>No incidents recorded</p> :
            incidents.map(inc => (
              <div key={inc.incident_id} className="p-3">
                <div className="flex items-center justify-between mb-1">
                  <div className="flex items-center gap-2">
                    <span className="font-mono text-xs" style={{ color: C.blue }}>{inc.reference}</span>
                    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium capitalize" style={{ background: (impactColor[inc.impact] || C.slate) + '15', color: impactColor[inc.impact] || C.slate }}>{inc.impact}</span>
                    <span className="text-xs capitalize" style={{ color: C.slate }}>{inc.nature?.replace(/_/g, ' ')}</span>
                  </div>
                  <StatusDot status={inc.status} />
                </div>
                <p className="text-xs" style={{ color: C.navy }}>{inc.description}</p>
                {inc.resolution && <p className="text-[10px] mt-0.5" style={{ color: C.green }}>Resolution: {inc.resolution}</p>}
                <p className="text-[10px] mt-0.5" style={{ color: C.slate }}>{inc.date} | {inc.duration_minutes}m duration | By: {inc.reported_by}</p>
              </div>
            ))}
        </div>
      </Card>
    </div>
  );
}

// ─── Shared Components ───
function CPS230Module() {
  const api = useApi();
  const { user } = useAuth();
  const canAct = ['mga_admin', 'platform_owner'].includes(user?.role);
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [showLog, setShowLog] = useState(false);
  const [form, setForm] = useState({ title: '', operation: '', severity: 'non_material', description: '' });

  const load = useCallback(() => {
    api.get('/compliance/cps230/dashboard').then(r => setData(r.data)).catch(e => console.error('cps230:', e?.message)).finally(() => setLoading(false));
  }, [api]);
  useEffect(() => { load(); }, [load]);

  const logIncident = async () => {
    try {
      const { data: inc } = await api.post('/compliance/cps230/incidents', form);
      toast.success(`Incident ${inc.reference} logged${inc.severity === 'material' ? ' — APRA notification due within 72h' : ''}`);
      setShowLog(false); setForm({ title: '', operation: '', severity: 'non_material', description: '' });
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || 'Failed to log incident'); }
  };

  const act = async (fn, msg) => {
    try { await fn(); toast.success(msg); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || 'Action failed'); }
  };

  if (loading || !data) return <Skeleton className="h-40 rounded-sm" />;
  const opStatus = { pass: C.green, warn: C.amber, fail: C.red };
  const critColor = { critical: C.red, important: C.amber, supporting: C.slate };
  const now = new Date();

  return (
    <div className="space-y-4" data-testid="cps230-module">
      <p className="text-xs" style={{ color: C.slate }}>{data.standard} — operational risk profile, critical-operation tolerance levels, material service provider register and 72-hour material incident notification.</p>

      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <MiniKPI label="Ops Within Tolerance" value={`${data.ops_within_tolerance}/${data.ops_total}`} color={data.ops_within_tolerance === data.ops_total ? C.green : C.red} />
        <MiniKPI label="Material Providers" value={data.material_providers} color={C.blue} />
        <MiniKPI label="Open Incidents" value={data.open_incidents} color={data.open_incidents > 0 ? C.amber : C.green} />
        <MiniKPI label="APRA Notices Overdue" value={data.apra_notifications_overdue} color={data.apra_notifications_overdue > 0 ? C.red : C.green} />
      </div>

      {/* Critical operations tolerance */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white overflow-hidden" data-testid="cps230-critical-ops">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)]">
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Critical Operations — Tolerance Levels</h3>
        </div>
        <table className="w-full text-left">
          <thead><tr style={{ background: C.bg }}>
            {['Operation', 'Tolerance', 'Current', 'Status', 'Basis'].map(h => <th key={h} className="py-2 px-3 text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {data.critical_operations.map(o => (
              <tr key={o.operation} className="border-b border-[rgba(136,150,168,0.08)]">
                <td className="py-2 px-3 text-xs font-medium" style={{ color: C.navy }}>{o.operation}</td>
                <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{o.tolerance}</td>
                <td className="py-2 px-3 text-xs font-mono" style={{ color: C.navy }}>{o.current}</td>
                <td className="py-2 px-3"><span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: opStatus[o.status] + '15', color: opStatus[o.status] }}>{o.status}</span></td>
                <td className="py-2 px-3 text-[10px]" style={{ color: C.slate }}>{o.basis}</td>
              </tr>
            ))}
          </tbody>
        </table>
      </Card>

      {/* Material service providers */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white overflow-hidden" data-testid="cps230-providers">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)] flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Material Service Provider Register</h3>
          {data.overdue_provider_reviews > 0 && <span className="text-[10px] font-semibold" style={{ color: C.red }}>{data.overdue_provider_reviews} review(s) overdue</span>}
        </div>
        <table className="w-full text-left">
          <thead><tr style={{ background: C.bg }}>
            {['Provider', 'Service', 'Criticality', 'Last Assessed', 'Next Due', ''].map((h, i) => <th key={i} className="py-2 px-3 text-[11px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{h}</th>)}
          </tr></thead>
          <tbody>
            {data.providers.map(p => {
              const overdue = new Date(p.next_assessment_due) < now;
              return (
                <tr key={p.provider_id} className="border-b border-[rgba(136,150,168,0.08)]" data-testid={`cps230-provider-${p.provider_id}`}>
                  <td className="py-2 px-3 text-xs font-medium" style={{ color: C.navy }}>{p.name}</td>
                  <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{p.service}</td>
                  <td className="py-2 px-3"><span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: critColor[p.criticality] + '15', color: critColor[p.criticality] }}>{p.criticality}</span></td>
                  <td className="py-2 px-3 text-xs" style={{ color: C.slate }}>{new Date(p.last_assessment).toLocaleDateString('en-AU')}</td>
                  <td className="py-2 px-3 text-xs font-medium" style={{ color: overdue ? C.red : C.slate }}>{new Date(p.next_assessment_due).toLocaleDateString('en-AU')}{overdue ? ' — OVERDUE' : ''}</td>
                  <td className="py-2 px-3">
                    {canAct && (
                      <button onClick={() => act(() => api.post(`/compliance/cps230/providers/${p.provider_id}/review`), `${p.name} marked reviewed`)}
                        className="px-2 py-1 rounded text-[10px] font-medium" style={{ color: C.blue, border: '1px solid rgba(26,76,124,0.3)' }}
                        data-testid={`cps230-review-${p.provider_id}`}>Mark Reviewed</button>
                    )}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </Card>

      {/* Operational incidents */}
      <Card className="rounded-sm border border-[rgba(136,150,168,0.2)] bg-white" data-testid="cps230-incidents">
        <div className="p-3 border-b border-[rgba(136,150,168,0.15)] flex items-center justify-between">
          <h3 className="text-sm font-semibold" style={{ color: C.navy }}>Operational Incident Log</h3>
          {canAct && (
            <Button size="sm" onClick={() => setShowLog(!showLog)} style={{ background: C.blue }} className="text-xs" data-testid="cps230-log-incident-btn">
              {showLog ? 'Cancel' : 'Log Incident'}
            </Button>
          )}
        </div>
        {showLog && (
          <div className="p-4 border-b border-[rgba(136,150,168,0.12)] grid grid-cols-1 md:grid-cols-2 gap-2" style={{ background: C.bg }} data-testid="cps230-incident-form">
            <Input placeholder="Incident title *" value={form.title} onChange={e => setForm(f => ({ ...f, title: e.target.value }))} className="text-xs bg-white" data-testid="cps230-incident-title" />
            <select value={form.operation} onChange={e => setForm(f => ({ ...f, operation: e.target.value }))} className="px-3 py-2 text-xs rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.3)' }} data-testid="cps230-incident-operation">
              <option value="">Operation impacted…</option>
              {data.critical_operations.map(o => <option key={o.operation} value={o.operation}>{o.operation}</option>)}
            </select>
            <select value={form.severity} onChange={e => setForm(f => ({ ...f, severity: e.target.value }))} className="px-3 py-2 text-xs rounded-sm border bg-white" style={{ borderColor: 'rgba(136,150,168,0.3)' }} data-testid="cps230-incident-severity">
              <option value="non_material">Non-material</option>
              <option value="material">Material (APRA 72h notification)</option>
            </select>
            <Input placeholder="Description" value={form.description} onChange={e => setForm(f => ({ ...f, description: e.target.value }))} className="text-xs bg-white" data-testid="cps230-incident-desc" />
            <div><Button size="sm" onClick={logIncident} disabled={!form.title.trim()} style={{ background: C.navy }} className="text-xs" data-testid="cps230-incident-submit">Log Incident</Button></div>
          </div>
        )}
        <div className="divide-y divide-[rgba(136,150,168,0.08)]">
          {data.incidents.length === 0 && <p className="p-4 text-xs" style={{ color: C.slate }}>No operational incidents recorded — a clean CPS 230 register.</p>}
          {data.incidents.map(i => {
            const notifyOverdue = i.severity === 'material' && !i.apra_notified && i.notify_due && new Date(i.notify_due) < now;
            return (
              <div key={i.incident_id} className="p-3 flex flex-wrap items-center gap-2" data-testid={`cps230-incident-${i.incident_id}`}>
                <span className="font-mono text-xs" style={{ color: C.blue }}>{i.reference}</span>
                <span className="text-xs font-medium flex-1 min-w-[160px]" style={{ color: C.navy }}>{i.title}</span>
                {i.operation && <span className="text-[10px]" style={{ color: C.slate }}>{i.operation}</span>}
                <span className="px-2 py-0.5 rounded text-[10px] font-bold uppercase" style={{ background: (i.severity === 'material' ? C.red : C.slate) + '15', color: i.severity === 'material' ? C.red : C.slate }}>{i.severity.replace('_', '-')}</span>
                <span className="px-2 py-0.5 rounded text-[10px] font-semibold uppercase" style={{ background: (i.status === 'open' ? C.amber : C.green) + '15', color: i.status === 'open' ? C.amber : C.green }}>{i.status}</span>
                {i.severity === 'material' && i.status === 'open' && (
                  i.apra_notified
                    ? <span className="text-[10px]" style={{ color: C.green }}>APRA notified</span>
                    : <span className="text-[10px] font-semibold" style={{ color: notifyOverdue ? C.red : C.amber }}>
                        APRA notice due {new Date(i.notify_due).toLocaleString('en-AU', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}{notifyOverdue ? ' — OVERDUE' : ''}
                      </span>
                )}
                {canAct && i.status === 'open' && (
                  <span className="flex gap-1.5">
                    {i.severity === 'material' && !i.apra_notified && (
                      <button onClick={() => act(() => api.put(`/compliance/cps230/incidents/${i.incident_id}`, { apra_notified: true }), 'APRA notification recorded')}
                        className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.red }} data-testid={`cps230-notify-${i.incident_id}`}>Record APRA Notice</button>
                    )}
                    <button onClick={() => act(() => api.put(`/compliance/cps230/incidents/${i.incident_id}`, { status: 'closed', resolution: 'Resolved and reviewed' }), 'Incident closed')}
                      className="px-2 py-1 rounded text-[10px] font-medium text-white" style={{ background: C.green }} data-testid={`cps230-close-${i.incident_id}`}>Close</button>
                  </span>
                )}
              </div>
            );
          })}
        </div>
      </Card>
    </div>
  );
}

function MiniKPI({ label, value, color }) {
  return (
    <Card className="p-3 rounded-sm border border-[rgba(136,150,168,0.15)] bg-white">
      <p className="text-[10px] uppercase tracking-wider font-medium" style={{ color: C.slate }}>{label}</p>
      <p className="text-base font-semibold mt-0.5" style={{ color: color || C.navy }}>{value}</p>
    </Card>
  );
}

function DetailRow({ label, value }) {
  return (
    <div>
      <p className="text-[10px] uppercase tracking-wider" style={{ color: C.slate }}>{label}</p>
      <p className="text-sm font-medium" style={{ color: C.navy }}>{value}</p>
    </div>
  );
}

function StatusDot({ status }) {
  const map = {
    clear: { color: C.green, label: 'Clear' },
    compliant: { color: C.green, label: 'OK' },
    pending: { color: C.amber, label: 'Pending' },
    suspended: { color: C.red, label: 'Suspended' },
    match_found: { color: C.red, label: 'Match' },
    identified: { color: C.red, label: 'Active' },
    assessing: { color: C.amber, label: 'Assessing' },
    received: { color: C.amber, label: 'Received' },
    processing: { color: C.teal, label: 'Processing' },
    sent: { color: C.green, label: 'Sent' },
    closed: { color: C.slate, label: 'Closed' },
    notified: { color: C.green, label: 'Notified' },
  };
  const cfg = map[status] || { color: C.slate, label: status || '—' };
  return (
    <span className="px-1.5 py-0.5 rounded text-[10px] font-medium" style={{ background: cfg.color + '15', color: cfg.color }}>
      {cfg.label}
    </span>
  );
}
